/** @typedef {import('headlight-configurable-application').Pict} Pict */

const NEW_ENTITY_ID = '__NEW__'; // special value to indicate a new entity is being created and not collide with other possible values
const SALT_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

//FIXME: should this subclass the shared entity selector?
const PictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;
const ScopedEntityData = require('./_ScopedEntityData.js');
const EntityDataBaseline = require('./_EntityDataBaseline.js');
const libFilterToggleUI = require('./_FilterToggleUI.js');
const libLabRouting = require('./_LabRouting.js');
const libSharedEntityRead = require('./_SharedEntityRead.js');

// Spec-set floors applied by entity: axis name -> resolver getter + the id column it floors.
// Materials, tests and pay items narrow through their DynamicFilters entries now; the lab axis
// is the one remaining set-membership floor. Extend by adding an axis here.
const SPEC_SET_AXIS =
{
	Lab: { getter: 'getAllowedLabIDs', field: 'IDLab' },
};

/**
 * A selection value as a set of identity strings: comma-separated or an array, blanks dropped.
 * @param {any} pValue
 * @return {Set<string>}
 */
function toIdentitySet(pValue)
{
	const tmpParts = Array.isArray(pValue) ? pValue : String(pValue ?? '').split(',');
	return new Set(tmpParts.map((pPart) => String(pPart ?? '').trim()).filter((pPart) => pPart.length > 0));
}

class MaterialsEntityInputType extends PictSectionInputExtension
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;

		// Latched true once any creation-enabled input initializes on this provider, so
		// onBeforeSaveDataAsync knows to fire the save-time EntityData projection.
		this._entityCreationEnabled = false;
	}

	/**
	 * Master opt-in gate for entity creation on this input. Reads PictForm.AllowEntityCreation,
	 * falling back to the provider option the creating subclasses set. Latches a provider-level
	 * flag so onBeforeSaveDataAsync fires the save-time EntityData projection even on a form with
	 * no sibling selector (e.g. a Test selector) that owns that trigger.
	 * @param {Object} pInput
	 * @return {boolean}
	 */
	_isEntityCreationEnabled(pInput)
	{
		const tmpEnabled = ((pInput?.PictForm?.AllowEntityCreation ?? this.options.AllowEntityCreation) === true);
		if (tmpEnabled)
		{
			this._entityCreationEnabled = true;
		}
		return tmpEnabled;
	}

	/**
	 * @param {string} pInputHTMLID
	 *
	 * @return {string}
	 */
	getSelectInputHTMLID(pInputHTMLID)
	{
		return `#MATERIALS-ENTITY-SELECT-FOR-${pInputHTMLID}`;
	}

	/**
	 * @param {string} pInputHTMLID
	 * @param {number|string} pRowIndex
	 *
	 * @return {string}
	 */
	getTabularSelectInputID(pInputHTMLID, pRowIndex)
	{
		return `#MATERIALS-ENTITY-SELECT-TABULAR-DATA-${pInputHTMLID}-${pRowIndex}`;
	}

	/**
	 * @param {string} pInputHTMLID
	 * @param {number|string} pRowIndex
	 *
	 * @return {string}
	 */
	getTabularSelectDropdownID(pInputHTMLID, pRowIndex)
	{
		return `#MATERIALS-ENTITY-SELECT-TABULAR-DROPDOWN-${pInputHTMLID}-${pRowIndex}`;
	}

	/**
	 * @param {string} pInputHTMLID
	 * @param {number|string} [pRowIndex]
	 * @param {string} [pType]
	 */
	getTrackingGUIDID(pInputHTMLID, pRowIndex, pType)
	{
		const tmpType = pType ? `${pType}-` : '';
		if (pRowIndex != null)
		{
			return `#MATERIALS-ENTITY-TRACKING-GUID-${tmpType}${pInputHTMLID}-${pRowIndex}`;
		}
		return `#MATERIALS-ENTITY-TRACKING-GUID-${tmpType}${pInputHTMLID}`;
	}

	/**
	 * Determines if a word starts with a leading vowel.
	 * @param {string} pWord The word to check.
	 * @returns {boolean} True if the word starts with a vowel, false otherwise.
	 */
	hasLeadingVowel(pWord)
	{
		if (typeof pWord !== 'string' || pWord.length === 0)
		{
			return false;
		}
		return [ 'A', 'E', 'I', 'O', 'U' ].includes(pWord.charAt(0).toUpperCase());
	}

	/**
	 * @param {import('./PictProvider-Select2Util.js').Select2AjaxOptions} pConfigOptions
	 * @param {Object} pView
	 * @param {Object} pInput
	 * @param {number} [pRowIndex]
	 */
	_updateSelect2Options(pConfigOptions, pView, pInput, pRowIndex)
	{
		pConfigOptions.GetRecordName = (pRecord) =>
		{
			return this._formatRecordForDisplay(pRecord, pInput);
		};
	}

	/**
	 * Append the spec-set floor for this descriptor's entity. The resolver getter
	 * returns null on the fail-open paths (no contract FK, empty set, no join rows),
	 * in which case pFilters is left unchanged.
	 *
	 * Off-switch: PictForm.DisableSpecYearSiloing.
	 *
	 * Subclasses that bind Entity in the constructor (e.g. MaterialsLabSelector →
	 * 'Lab') are routed correctly via _getEntity(). Every other entity narrows
	 * through its DynamicFilters entries instead and gets nothing here.
	 *
	 * @param {Array<string>} pFilters
	 * @param {Object} pInput
	 * @param {Array<string>} [pBackoff] - Optional back-off-able filter list. When provided, the clause
	 *   routes here (self-omits on an empty result + override); when omitted it is mandatory (pFilters).
	 *   The call site (initializeSelect2) passes it only for descriptors that opt in via
	 *   PictForm.SpecYearBackOff, so the floor is mandatory by default.
	 * @param {number|string} [pRowIndex] - Row index for a tabular picker, so the Show All check below
	 *   reads THIS row's toggle state rather than the flat (non-tabular) address.
	 *
	 * @return {Array<string>}
	 */
	localHandleSearchFilters(pFilters, pInput, pBackoff, pRowIndex)
	{
		pFilters = pFilters || [];
		if (pInput?.PictForm?.DisableSpecYearSiloing || !this.pict.providers.SpecScopeResolver)
		{
			return pFilters;
		}
		const tmpAxis = SPEC_SET_AXIS[this._getEntity(pInput)];
		// Lab is the only axis here, and its floor must be read family-expanded: spec-set membership is
		// declared per FAMILY, so raw IDLab membership empties one level or the other (see
		// _LabRouting.resolveSpecSetLabFloor).
		const tmpAllowed = tmpAxis ? libLabRouting.resolveSpecSetLabFloor(this.pict) : null;
		if (tmpAllowed && tmpAllowed.size > 0 && (pInput?.PictForm?.SpecYearMandatory || !this.pict.providers.Select2Util?.isFilterOverridden?.(pInput, pRowIndex)))
		{
			// Honor pBackoff when the caller provides it; otherwise the clause is mandatory
			// (pFilters) — it stays applied even when it empties a search, so an out-of-spec search
			// trips the no-results signal instead of silently widening. The "Show all" override drops
			// it unless PictForm.SpecYearMandatory is set.
			(Array.isArray(pBackoff) ? pBackoff : pFilters).push(`FBL~${tmpAxis.field}~INN~${Array.from(tmpAllowed).join(',')}`);
		}
		return pFilters;
	}

	/**
	 * Initialize the Select2 plugin for the input element.
	 *
	 * @param {Object} pView
	 * @param {Record<string, any>} pInput - The input object.
	 * @param {number} [pRowIndex] - The row index (optional, for tabular inputs).
	 */
	initializeSelect2(pView, pInput, pRowIndex)
	{
		if (window['jQuery'] && window['jQuery'].fn.select2)
		{
			const tmpEntity = this._getEntity(pInput);
			// Human-readable entity label for the placeholder: de-camelCase the entity name so it reads with
			// spaces (SampleType -> "Sample Type", MixDesign -> "Mix Design", ProductionPlant -> "Production
			// Plant", LineItem -> "Line Item"). The baked input types own this; it is not a manifest concern.
			const tmpEntityLabel = String(tmpEntity || '').replace(/([a-z0-9])([A-Z])/g, '$1 $2');
			/** @type {import('./PictProvider-Select2Util.js').Select2AjaxOptions} */
			const tmpConfigOptions = {
				Multiple: pInput.PictForm?.Multiple == true,
				Input: pInput,
				SearchFallbackPlaceholder: `Select ${ this.hasLeadingVowel(tmpEntityLabel) ? 'an' : 'a' } ${tmpEntityLabel}...`,
				ApplySort: true,
				JoinEntity: pInput.PictForm.JoinEntity,
				JoinEntityFirst: pInput.PictForm.JoinEntityFirst ?? true,
				JoinField: pInput.PictForm.JoinField ? pInput.PictForm.JoinField : pInput.PictForm.JoinEntity ? `ID${ pInput.PictForm.JoinEntity }` : null,
				EntityDisplayField: pInput.PictForm.EntityDisplayField,
				// Honor descriptor SearchFields (e.g. ['Name','ManufacturerCode']) so a picker is searchable by
				// the code shown in its chip, not just Name. Subclass _updateSelect2Options may still override.
				SearchFields: pInput.PictForm.SearchFields,
				JoinEntityDisplayField: pInput.PictForm.JoinEntityDisplayField,
				Disabled: pInput.PictForm.Disabled || false,
				GetRecordID: (pEntity) => pEntity?.[this._getRecordIdentity(pInput)] || '',
				// Threaded so Select2Util.applyDynamicFilters can resolve a per-row
				// FromRowField against THIS row's sibling cell (each row's ajax closure
				// captures its own RowIndex). Undefined for non-tabular pickers.
				RowIndex: pRowIndex,
				View: pView,
			};
			// give child classes an opportunity to modify the select2 options
			this._updateSelect2Options(tmpConfigOptions, pView, pInput, pRowIndex);
			// Layer the spec-set floor over whatever HandleSearchFilters the
			// subclass installed. Subclasses replace HandleSearchFilters
			// wholesale (e.g. to force Active=1) rather than chaining, so
			// we wrap here once after _updateSelect2Options runs. Filters
			// the subclass added are evaluated first; the floor then narrows
			// the result. Off-switch handled inside localHandleSearchFilters
			// via PictForm.DisableSpecYearSiloing.
			const tmpSubclassHandleSearchFilters = tmpConfigOptions.HandleSearchFilters;
			tmpConfigOptions.HandleSearchFilters = (pFilters, pParams, pBackoff) =>
			{
				let tmpFilters = pFilters || [];
				if (typeof tmpSubclassHandleSearchFilters === 'function')
				{
					tmpFilters = tmpSubclassHandleSearchFilters(tmpFilters, pParams, pBackoff) || tmpFilters;
				}
				// The floor is mandatory by default; a descriptor opts into a back-off-able one (self-omit
				// on empty) via PictForm.SpecYearBackOff, which routes the clause into the back-off list.
				return this.localHandleSearchFilters(tmpFilters, pInput, pInput?.PictForm?.SpecYearBackOff ? pBackoff : undefined, pRowIndex);
			};
			const methodNameByContext = (pRowIndex || pRowIndex === 0) ? 'getTabularSelectDropdownID' : 'getSelectInputHTMLID';
			const tmpSelectElementSelector = this[methodNameByContext](pInput.Macro.RawHTMLID, pRowIndex);
			if (tmpConfigOptions.Multiple)
			{
				this.pict.ContentAssignment.setAttribute(tmpSelectElementSelector, 'multiple', 'multiple');
				const tmpSelectWrapper = document.querySelector(tmpSelectElementSelector)?.parentElement;
				if (tmpSelectWrapper && tmpSelectWrapper.classList.contains('select'))
				{
					tmpSelectWrapper.classList.remove('select');
					if (!tmpSelectWrapper.classList.contains('is-multiple'))
					{
						tmpSelectWrapper.classList.add('is-multiple');
					}
				}
				// Strip the placeholder option -- but ONLY on a first initialization. This runs
				// on every initializeSelect2 call, and a re-init (any onAfterRender re-runs
				// onInputInitialize for every input in the section) would otherwise wipe the
				// user's real selections here and leave the picker visibly EMPTY until the
				// marshal repopulated it. Measured on LADOTD Sample Creation: removing one
				// test blanked the Tests picker for ~212ms (tags 1 -> 0 -> 1). Gate it on the
				// same condition the widget creation below uses, so first-init is unchanged.
				if (!window['jQuery'](tmpSelectElementSelector).data('select2'))
				{
					window['jQuery'](tmpSelectElementSelector).empty();
				}
			}
			if (this._isEntityCreationEnabled(pInput))
			{
				tmpConfigOptions.GetPinnedItem = () =>
				{
					return { id: NEW_ENTITY_ID, text: `New ${this._getEntity(pInput)}...` };
				}; //FIXME: human readable entity...
			}
			// if an EntityTag is defined, use it to enhance the search and display
			if (pInput.PictForm.EntityTag)
			{
				tmpConfigOptions.GetRecordUserFacingID = (pEntity) =>
				{
					return pEntity?.[pInput.PictForm.EntityTag] || 'N/A';
				};
			}
			/** @type {import('select2').Options & { [key: string]: any }} */
			const tmpSelectOptions = this.pict.providers.Select2Util.getOptionsAjax(tmpEntity, tmpConfigOptions);
			if (pInput.PictForm.SelectOptionsPickList && this.pict.providers.DynamicMetaLists.listDefinitions[pInput.PictForm.SelectOptionsPickList]?.Dynamic)
			{
				tmpSelectOptions.ajax = {}; // necessary for custom data adapter to be used
				tmpSelectOptions.dataAdapter = $.fn.select2.amd.require('select2/data/dynamicLocalPaginationAdapter');
				// plumbing necessary context for the adapter since it doesn't have access to pict
				tmpSelectOptions.Input = pInput;
				tmpSelectOptions.DynamicMetaLists = this.pict.providers.DynamicMetaLists; //FIXME: ...
			}
			// tabular vs. non-tabular context
			// if the select2 plugin is not already initialized, initialize it now
			if (!window['jQuery'](tmpSelectElementSelector).data('select2'))
			{
				window['jQuery'](tmpSelectElementSelector).select2(tmpSelectOptions);
			}
		}
	}

	/**
	 * Initializes the input element for the Pict provider select input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 *
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {boolean} - Returns true if the input element is successfully initialized, false otherwise.
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		if (this.pict.providers.DynamicInput)
		{
			this.pict.providers.DynamicInput.registerIgnoredEventHash('change.select2');
		}
		this._generateSaltIfNeeded(pView, pInput);
		this._scheduleBaselineCapture(pView, pTransactionGUID);
		// initialize the select2 plugin
		this.initializeSelect2(pView, pInput);
		if (this._shouldApplyConfiguredDefault(pInput, pValue))
		{
			// Empty picker on a new form — apply the descriptor Default (resolving it via DefaultField if set). The
			// async resolve's setDataByInput lands the id and repaints. Blank pValue so that, in the rare case the
			// value arrived as a raw token, super / _refreshRecord don't try to load a record keyed by it.
			this._applyConfiguredDefault(pView, pInput, null);
			pValue = '';
		}
		const tmpResult = super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		this._refreshRecord(pView, pInput, pValue, this._getEntity(pInput), pTransactionGUID, this._getRecordIdentity(pInput));
		if (pInput.PictForm?.DetectDesync && pValue)
		{
			this._checkRecordDesync(pView, pInput).catch((pError) =>
			{
				this.pict.log.warn(`MaterialsEntitySelector de-sync check failed: ${pError.message || pError}`);
			});
		}
		if (pInput.PictForm?.ShowAllToggle)
		{
			this._updateFilterToggleIcon(pInput.Macro.RawHTMLID, !!this.pict.providers.Select2Util?.isFilterOverridden?.(pInput));
			this._syncFilterToggleDisabledState(pInput);
		}
		// only put a default sample in if we are hidden, or user selected "new record"
		if (!pValue && (pInput.PictForm.InputType.startsWith('MaterialsHidden') || pInput.PictForm.DefaultToNewRecord) || pValue === NEW_ENTITY_ID)
		{
			pValue = this._handleDefaultRecord(pView, pInput, pValue, pTransactionGUID);
		}
		return tmpResult;
	}

	/**
	 * @param {string} pInputHTMLID
	 * @return {string} the integrated "Show All" filter-toggle element ID for this picker.
	 */
	getSelectFilterToggleID(pInputHTMLID, pRowIndex)
	{
		return `#MATERIALS-ENTITY-FILTER-TOGGLE-${pInputHTMLID}${(pRowIndex != null) ? `-${pRowIndex}` : ''}`;
	}

	/**
	 * Flip the integrated "Show All" override for this picker. The state persists at `<key>__ShowAll`
	 * (non-tabular) or `<key>__ShowAll_<rowIndex>` (tabular, per-row), read by Select2Util.isFilterOverridden,
	 * so the next option fetch drops or re-applies the scoped filters.
	 * @param {string} pInputHTMLID
	 * @param {string} pKey - state key: the DataAddress (non-tabular) or the input Hash (tabular per-row).
	 * @param {number|string} [pRowIndex] - present for a tabular per-row toggle.
	 */
	toggleFilterOverride(pInputHTMLID, pKey, pRowIndex)
	{
		// A read-only / disabled selector paints the toggle inert (_FilterToggleUI) — swallow the click rather
		// than writing override state and re-running the option fetch for a field whose value cannot change.
		if (libFilterToggleUI.isToggleDisabled(this.pict, this.getSelectFilterToggleID(pInputHTMLID, pRowIndex)))
		{
			return;
		}
		const tmpSuffix = (pRowIndex != null) ? `__ShowAll_${pRowIndex}` : '__ShowAll';
		const tmpAddress = `AppData.DocumentData.ReportData.FormData.${pKey}${tmpSuffix}`;
		const tmpNewState = !this.pict.manifest.getValueByHash(this.pict, tmpAddress);
		this.pict.manifest.setValueByHash(this.pict, tmpAddress, tmpNewState);
		this._updateFilterToggleIcon(pInputHTMLID, tmpNewState, pRowIndex);
	}

	/**
	 * Paint the toggle inert when the field is read-only / disabled. The render-time `{~FilterToggleDisabled:~}`
	 * macro already covers a manifest-declared Disabled; this catches one set AFTER render — `MaterialsSampleSelector`
	 * flips PictForm.Disabled from `DisabledOnceSaved` inside its own onInputInitialize.
	 * @param {Object} pInput @param {number|string} [pRowIndex]
	 */
	_syncFilterToggleDisabledState(pInput, pRowIndex)
	{
		libFilterToggleUI.syncToggleDisabledState(this.pict, this.getSelectFilterToggleID(pInput.Macro.RawHTMLID, pRowIndex), libFilterToggleUI.isInputDisabled(pInput));
	}

	/**
	 * Repaint the toggle icon to reflect the current override state.
	 * @param {string} pInputHTMLID
	 * @param {boolean} pState
	 * @param {number|string} [pRowIndex]
	 */
	_updateFilterToggleIcon(pInputHTMLID, pState, pRowIndex)
	{
		if (!this.pict?.ContentAssignment)
		{
			return;
		}
		const tmpIcon = pState ? 'fa-toggle-on' : 'fa-toggle-off';
		this.pict.ContentAssignment.assignContent(`${this.getSelectFilterToggleID(pInputHTMLID, pRowIndex)} > .icon`, `<i class="fas ${tmpIcon}"></i>`);
	}

	_getTrackingGUID(pInput, pRowIndex, pType)
	{
		if (pRowIndex == null)
		{
			switch (pType)
			{
			case 'TEST':
				if (pInput.PictForm?.TestTrackingGUID && pInput.PictForm.TestTrackingGUID != true)
				{
					return pInput.PictForm.TestTrackingGUID;
				}
			default:
				if (pInput.PictForm?.SampleTrackingGUID && pInput.PictForm.SampleTrackingGUID != true)
				{
					return pInput.PictForm.SampleTrackingGUID;
				}
			}
		}
		return this.pict.ContentAssignment.readContent(this.getTrackingGUIDID(pInput.Macro.RawHTMLID, pRowIndex, pType));
	}

	_setTrackingGUID(pInput, pRowIndex, pTrackingGUID, pType)
	{
		this.pict.ContentAssignment.assignContent(this.getTrackingGUIDID(pInput.Macro.RawHTMLID, pRowIndex, pType), pTrackingGUID);
	}

	_bindTrackingGUIDIfNeeded(pView, pGroup, pInput, pRowIndex)
	{
		if (this._getTrackingGUID(pInput, pRowIndex))
		{
			return;
		}
		if (this._getEntity(pInput) == 'Sample')
		{
			return;
		}
		// modalities:
		//
		// single global sample
		// tabular row-based tests
		// tabular row-based sample and tests

		if (pRowIndex != null)
		{
			// tabular
			const tmpLocalManifest = pGroup.supportingManifest;
			if (!tmpLocalManifest || typeof tmpLocalManifest !== 'object' || Array.isArray(tmpLocalManifest))
			{
				this.log.error('MaterialsEntitySelector: invalid supporting manifest on group.', { pGroup });
			}
			else
			{
				for (const tmpDescriptor of Object.values(tmpLocalManifest.elementDescriptors))
				{
					if (tmpDescriptor.PictForm && tmpDescriptor.PictForm.SampleTrackingGUID)
					{
						const tmpTrackingGUID = this._getTrackingGUID(tmpDescriptor, pRowIndex);
						if (tmpTrackingGUID)
						{
							this._setTrackingGUID(pInput, pRowIndex, tmpTrackingGUID);
							return;
						}
					}
				}
			}
		}
		// check sibling Descriptors for a sample descriptor
		const tmpGlobalManifest = this.pict.views.PictFormMetacontroller?.manifestDescription;
		if (!tmpGlobalManifest || !tmpGlobalManifest.Descriptors || typeof tmpGlobalManifest.Descriptors !== 'object' || Array.isArray(tmpGlobalManifest.Descriptors))
		{
			this.log.error('MaterialsEntitySelector: invalid global manifest on metacontroller.', { pGroup });
			return;
		}
		for (const tmpDescriptor of Object.values(tmpGlobalManifest.Descriptors))
		{
			if (tmpDescriptor.PictForm && tmpDescriptor.PictForm.SampleTrackingGUID)
			{
				const tmpTrackingGUID = this._getTrackingGUID(tmpDescriptor, null);
				if (tmpTrackingGUID)
				{
					this._setTrackingGUID(pInput, pRowIndex, tmpTrackingGUID);
					return;
				}
			}
		}
		if (!this._getTrackingGUID(pInput, pRowIndex))
		{
			this.log.warn('MaterialsEntitySelector: could not find sibling sample descriptor to bind tracking GUID from.');
		}
	}

	/**
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @param {number} [pRowIndex] - The row index parameter. If present, this is a tabular input.
	 *
	 * @return {string}
	 */
	_handleDefaultRecord(pView, pInput, pValue, pTransactionGUID, pRowIndex)
	{
		//FIXME: should not do this on every marshall - only on initial value marshall - move to on-init? does that flow in well with the initial value coming in or do those clash?
		let tmpDefaultRecord = this._getDefaultRecord(pView, pInput, pRowIndex);
		if (tmpDefaultRecord)
		{
			tmpDefaultRecord = JSON.parse(JSON.stringify(tmpDefaultRecord));
			this._translateRecordTemplateFields(tmpDefaultRecord);
			const tmpDefaultID = tmpDefaultRecord[this._getRecordIdentity(pInput)];
			pValue = tmpDefaultID || null;
			if (!pValue)
			{
				//FIXME: some kind of negative ID thing to maintain uniqueness until we save? figure that out
			}
			if (pValue)
			{
				//FIXME: is this right? it seems weird but copied from another input provider; debug it
				this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), pInput.PictForm.InformaryDataAddress, pValue);
			}
			this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), this._getRecordAddress(pView, pInput, pRowIndex), tmpDefaultRecord);
		}
		if (pValue)
		{
			// don't need to do this if we didn't synthesize a value, since it already had no value
			this._refreshRecord(pView, pInput, pValue, this._getEntity(pInput), pTransactionGUID, this._getRecordIdentity(pInput), pRowIndex, (pRecord) =>
			{
				if (pRecord)
				{
					//FIXME: if this finds a value, what should we do? iterate our uniqueness? push this value back into the sample selector? how do we tell select2 that we changed the value?
					/*
					if (pRowIndex != null)
					{
						this.pict.ContentAssignment.assignContent(this.getTabularSelectInputID(pInput.Macro.RawHTMLID, pRowIndex), pRecord[`ID${this._getEntity(pInput)}`]);
					}
					else
					{
						this.pict.ContentAssignment.assignContent(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID), pRecord[`ID${this._getEntity(pInput)}`]);
					}*/
					if (pRowIndex != null)
					{
						this.pict.ContentAssignment.assignContent(this.getTabularSelectInputID(pInput.Macro.RawHTMLID, pRowIndex), pRecord[this._getRecordIdentity(pInput)]);
					}
					else
					{
						this.pict.ContentAssignment.assignContent(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID), pRecord[this._getRecordIdentity(pInput)]);
					}
					//FIXME: how we get select2 to update?
				}
			});
		}
		return pValue;
	}


	/**
	 * @param {Object} pView
	 * @param {Object} pInput
	 * @param {number|string} [pRowIndex]
	 */
	_getDefaultRecord(pView, pInput, pRowIndex)
	{
		let tmpRecord = pInput.PictForm?.DefaultRecord;
		// When creation is enabled, mint a client GUID<Entity> so a synthesized record has a stable,
		// trackable identity before it is saved. Opt out via PictForm.GenerateGUID = false when the
		// caller supplies the GUID another way. The stanza segment defaults to the entity name and is
		// overridable via PictForm.GUIDEntityTypeStanza (e.g. the Sample subclass uses 'S'). The raw
		// template is translated downstream in _handleDefaultRecord.
		if (this._isEntityCreationEnabled(pInput) && pInput.PictForm?.GenerateGUID !== false)
		{
			const tmpEntity = this._getEntity(pInput);
			const tmpGUIDField = `GUID${tmpEntity}`;
			tmpRecord = (tmpRecord && typeof tmpRecord === 'object' && !Array.isArray(tmpRecord)) ? JSON.parse(JSON.stringify(tmpRecord)) : { };
			if (!tmpRecord[tmpGUIDField])
			{
				const tmpStanza = pInput.PictForm?.GUIDEntityTypeStanza || tmpEntity;
				tmpRecord[tmpGUIDField] = `{~D:AppData.DocumentData.ReportData.GUIDDocument~}-${tmpStanza}-${this._getSalt(pView, pInput, pRowIndex)}`;
			}
		}
		return tmpRecord;
	}

	_getRecordSimpleIdentity(pInput)
	{
		return `ID${this._getEntity(pInput)}`;
	}

	_getRecordIdentity(pInput)
	{
		return pInput?.PictForm?.RecordIdentity || this._getDefaultRecordIdentity(pInput);
	}

	_getDefaultRecordIdentity(pInput)
	{
		// A creation-enabled selector tracks records by GUID<Entity> (a new record has no numeric
		// ID<Entity> yet), mirroring the Sample subclass's hard-coded GUIDSample identity. Still
		// overridable per input via PictForm.RecordIdentity. Non-creating selectors keep ID<Entity>.
		if (this._isEntityCreationEnabled(pInput))
		{
			return `GUID${this._getEntity(pInput)}`;
		}
		return this._getRecordSimpleIdentity(pInput);
	}

	/**
	 * @param {Object} pView
	 * @param {Object} pInput
	 * @param {number|string|null} [pRowIndex]
	 * @param {boolean} [pForce]
	 */
	_generateSaltIfNeeded(pView, pInput, pRowIndex, pForce)
	{
		const tmpSaltAddress = this._getRecordAddress(pView, pInput, pRowIndex, '__Salt');
		let tmpSalt = !pForce && this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpSaltAddress);
		if (!tmpSalt)
		{
			tmpSalt = this._generateSaltStr();

			this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpSaltAddress, tmpSalt);
		}
	}

	/**
	 * @param {Object} pView
	 * @param {Object} pInput
	 * @param {number|string} [pRowIndex]
	 * @return {string} the salt to identify this input instance
	 */
	_getSalt(pView, pInput, pRowIndex)
	{
		const tmpSaltAddress = this._getRecordAddress(pView, pInput, pRowIndex, '__Salt');
		return this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpSaltAddress);
	}

	/**
	 * @param {number} [pLength=8]
	 * @return {string} a random salt to identify this input instance
	 */
	_generateSaltStr(pLength = 8)
	{
		let result = '';
		for (let i = 0; i < pLength; i++)
		{
			result += SALT_CHARS.charAt(Math.floor(Math.random() * SALT_CHARS.length));
		}
		return result;
	}

	/**
	 * @param {Object} pView
	 * @param {Object} pInput
	 * @param {number|string} [pRowIndex]
	 * @param {string} [pSuffix]
	 */
	_getRecordAddress(pView, pInput, pRowIndex, pSuffix = '__Record')
	{
		if (pInput.PictForm?.RecordAddress)
		{
			return pInput.PictForm.RecordAddress;
		}
		if (pRowIndex != null)
		{
			return pView.pict.providers.Informary.getComposedContainerAddress(pInput.PictForm.InformaryContainerAddress, pRowIndex, pInput.PictForm.InformaryDataAddress) + pSuffix;
			//FIXME: cute thing but not supported by setValueAtAddress in Manyfest
			//const tmpTestAddress = pView.pict.providers.Informary.getComposedContainerAddress(pInput.PictForm.InformaryContainerAddress, pRowIndex, pInput.PictForm.InformaryDataAddress) + '..__' + pInput.DataAddress;
			/** @type {string} */
			const tmpTestAddress = pView.pict.providers.Informary.getComposedContainerAddress(pInput.PictForm.InformaryContainerAddress, pRowIndex, pInput.PictForm.InformaryDataAddress);
			const tmpSplitPoint = tmpTestAddress.lastIndexOf('.');
			if (tmpSplitPoint >= 0)
			{
				return tmpTestAddress.substring(0, tmpSplitPoint + 1) + '__' + tmpTestAddress.substring(tmpSplitPoint + 1);
			}

			return `__${tmpTestAddress}`;
		}
		//FIXME: if pRowIndex is given, this is tabular and we need to adjust the address accordingly? unless, we can apply this in the local tabular context
		return `${pInput.DataAddress || pInput.Hash}${pSuffix}`;
	}

	/**
	 * @param {Record<string, any>} pInput
	 */
	_getEntity(pInput)
	{
		return (pInput?.PictForm?.Entity) || this.options?.Entity || null;
	}

	/**
	 * Resolve the VALUE (not __Record) data address for this input/row -- the same address the
	 * marshal binds the select element to.
	 * @param {Object} pView @param {Object} pInput @param {number|string|null} [pRowIndex]
	 * @return {string}
	 */
	_getValueAddress(pView, pInput, pRowIndex)
	{
		if (pRowIndex != null)
		{
			return pView.pict.providers.Informary.getComposedContainerAddress(pInput.PictForm.InformaryContainerAddress, pRowIndex, pInput.PictForm.InformaryDataAddress);
		}
		return pInput.PictForm?.InformaryDataAddress || pInput.DataAddress || pInput.Hash;
	}

	/**
	 * Normalize a CLEARED entity selection. select2's clear ("x") marshals the literal string
	 * "null" (or null) into the value field; left as-is it persists in saved FormData and prints
	 * as "null" in the exported PDF, and _refreshRecord treats "null" as a real id so the previous
	 * __Record is never cleared (a stale record then drives downstream displays). Coerce a cleared
	 * value to '' and correct the value the marshal already wrote, so the caller's _refreshRecord('')
	 * takes its clear branch and resets __Record.
	 * @param {Object} pView @param {Object} pInput @param {any} pValue @param {number} [pRowIndex]
	 * @return {any} the normalized value ('' when cleared, otherwise pValue unchanged)
	 */
	_coerceClearedEntityValue(pView, pInput, pValue, pRowIndex)
	{
		if ((pValue === 'null') || (pValue === 'undefined') || (pValue === null) || (typeof pValue === 'undefined'))
		{
			this._handleEntitySelectionCleared(pView, pInput, pRowIndex);
			this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), this._getValueAddress(pView, pInput, pRowIndex), '');
			return '';
		}
		return pValue;
	}

	/**
	 * Initializes a tabular input element.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The index of the row.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {any} - The result of the initialization.
	 */
	onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		if (this.pict.providers.DynamicInput)
		{
			this.pict.providers.DynamicInput.registerIgnoredEventHash('change.select2');
		}
		this._generateSaltIfNeeded(pView, pInput, pRowIndex);
		this._scheduleBaselineCapture(pView, pTransactionGUID);
		this.initializeSelect2(pView, pInput, pRowIndex);
		if (this._shouldApplyConfiguredDefault(pInput, pValue))
		{
			// See onInputInitialize — apply the Default on an empty picker (blank pValue so a raw token, if any,
			// isn't loaded as a record key).
			this._applyConfiguredDefault(pView, pInput, pRowIndex);
			pValue = '';
		}
		this._refreshRecord(pView, pInput, pValue, this._getEntity(pInput), pTransactionGUID, this._getRecordIdentity(pInput), pRowIndex);
		if (pInput.PictForm?.ShowAllToggle)
		{
			this._updateFilterToggleIcon(pInput.Macro.RawHTMLID, !!this.pict.providers.Select2Util?.isFilterOverridden?.(pInput, pRowIndex), pRowIndex);
			this._syncFilterToggleDisabledState(pInput, pRowIndex);
		}
		// only put a default sample in if we are hidden, or user selected "new record"
		if (!pValue && (pInput.PictForm.InputType.startsWith('MaterialsHidden') || pInput.PictForm.DefaultToNewRecord) || pValue === NEW_ENTITY_ID)
		{
			pValue = this._handleDefaultRecord(pView, pInput, pValue, pTransactionGUID, pRowIndex);
		}
		return super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * True when a declarative `Default` should be applied to the picker. Fires when the picker is empty — the new
	 * form's marshal destination holds `''` (a DEFINED empty string) at the address, so manyfest's getValueByHash
	 * does NOT substitute the descriptor Default (it only does so for `undefined`); `pValue` therefore arrives as
	 * `''` and we apply the Default ourselves. Also fires if `pValue` DID arrive as the raw Default token (from an
	 * undefined address) for a by-field default, so that token can be resolved. Mirrors the value-gate
	 * `_handleDefaultRecord` uses — once a value is set the gate is false and it does not re-fire.
	 *
	 * @param {Object} pInput @param {any} pValue @return {boolean}
	 */
	_shouldApplyConfiguredDefault(pInput, pValue)
	{
		if (pInput.Default == null || pInput.Default === '')
		{
			return false;
		}
		if (pValue == null || pValue === '')
		{
			return true;
		}
		// Non-empty but equal to the raw Default token (manyfest surfaced it at an undefined address) — resolve it,
		// but only for a by-field default; a by-id value already IS the record id and must not re-apply.
		const tmpField = pInput.PictForm ? pInput.PictForm.DefaultField : null;
		return (pValue === pInput.Default && !!tmpField && tmpField !== this._getRecordIdentity(pInput));
	}

	/**
	 * Apply `pInput.Default` to an empty picker. `PictForm.DefaultField` names a NON-identity field to match on
	 * (e.g. `Hash`) so the default is portable across customers — `Default:"ACPT"` + `DefaultField:"Hash"` resolves
	 * the customer's ACPT SampleType by its id via one read. Without a `DefaultField` (or when it IS the record
	 * identity) the `Default` is already the id and is set directly. `setDataByInput` writes the id into FormData,
	 * so the value-gate closes and this does not re-fire; a re-entrancy latch dedupes concurrent init passes while
	 * the read is in flight.
	 *
	 * @param {Object} pView @param {Object} pInput @param {number|null} [pRowIndex]
	 */
	_applyConfiguredDefault(pView, pInput, pRowIndex)
	{
		if (pInput._DefaultResolving)
		{
			return;
		}
		const tmpField = pInput.PictForm ? pInput.PictForm.DefaultField : null;
		if (!tmpField || tmpField === this._getRecordIdentity(pInput))
		{
			// Default is already the record id — set it directly, no resolve.
			this._setResolvedDefault(pView, pInput, pRowIndex, pInput.Default);
			return;
		}
		const tmpEntity = this._getEntity(pInput);
		if (!tmpEntity || !this.pict.HeadlightRestClient)
		{
			return;
		}
		pInput._DefaultResolving = true;
		this.pict.HeadlightRestClient.getJSON(`${tmpEntity}s/FilteredTo/FBV~${tmpField}~EQ~${encodeURIComponent(pInput.Default)}/0/1`, (pError, pRows) =>
		{
			pInput._DefaultResolving = false;
			const tmpRecord = (!pError && Array.isArray(pRows)) ? pRows[0] : null;
			if (tmpRecord)
			{
				this._setResolvedDefault(pView, pInput, pRowIndex, tmpRecord[this._getRecordIdentity(pInput)]);
			}
		});
	}

	/**
	 * Set a resolved default value onto the picker (single or tabular), which drives the record refresh + repaint.
	 * @param {Object} pView @param {Object} pInput @param {number|null} pRowIndex @param {any} pValue
	 */
	_setResolvedDefault(pView, pInput, pRowIndex, pValue)
	{
		if (pValue === undefined || pValue === null || pValue === '')
		{
			return;
		}
		if (pRowIndex != null)
		{
			pView.setDataTabularByHash(pInput.PictForm.GroupIndex, pInput.Hash, pRowIndex, pValue);
		}
		else
		{
			pView.setDataByInput(pInput, pValue);
		}
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {boolean}
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this._bindTrackingGUIDIfNeeded(pView, pGroup, pInput);
		//FIXME: base class doesn't allow this to be async - changed to run async safely for now, but not sure if this creates timing problems
		this._onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector).catch((pError) =>
		{
			this.pict.log.error(`Error in onDataMarshalToForm: ${pError}`);
		});
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The row index parameter.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {boolean}
	 */
	onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this._bindTrackingGUIDIfNeeded(pView, pGroup, pInput, pRowIndex);
		//FIXME: base class doesn't allow this to be async - changed to run async safely for now, but not sure if this creates timing problems
		this._onDataMarshalToForm(pView, pGroup, null, pInput, pValue, pHTMLSelector, pRowIndex).catch((pError) =>
		{
			this.pict.log.error(`Error in onDataMarshalToForm: ${pError}`);
		});
		return super.onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * Repaint this cell's select2 to the row's current value after a tabular row REORDER.
	 *
	 * A plain marshal (`_renderEntitiesToPicker`) only APPENDS a selected `<option>`; for a cell
	 * that already shows a value, appending the reordered-in value's option does not deselect the
	 * stale one, so select2 keeps displaying the old product. Here we make sure the option exists
	 * (append it from the local `__Record` if not) and then EXPLICITLY set the select's value,
	 * which switches the display. `change.select2` is UI-only, so this does not re-enter the
	 * framework's data-change/marshal cycle. This is what lets `_repaintAfterRowReorder` skip the
	 * expensive full source-view render (which re-initialized every select2).
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The row's current value for this input.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The row index.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {boolean}
	 */
	onTabularRowReorderRepaintTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		try
		{
			const tmpElementID = this.getTabularSelectDropdownID(pInput.Macro.RawHTMLID, pRowIndex);
			const tmpElements = this.pict.ContentAssignment.getElement(tmpElementID);
			if (!tmpElements || tmpElements.length < 1)
			{
				return true;
			}
			const tmpJQuery = window['jQuery'];
			// Cleared cell -> clear the picker.
			if (pValue == null || pValue === '' || pValue === 'null')
			{
				tmpJQuery(tmpElementID).val(null).trigger('change.select2');
				return true;
			}
			// Ensure the option for the row's current value is present (append it from the locally
			// held record when select2 doesn't already carry it), then force-select it.
			const tmpIdentity = this._getRecordIdentity(pInput);
			const tmpAlreadyPresent = Array.prototype.some.call(tmpElements[0].options || [], (pOption) => String(pOption.value) === String(pValue));
			if (!tmpAlreadyPresent)
			{
				const tmpRecord = this._getLocalRecordForValue(pView, pInput, pValue, pRowIndex);
				if (tmpRecord && (tmpRecord[tmpIdentity] != null))
				{
					tmpJQuery(tmpElementID).append(new Option(this._formatRecordForDisplay(tmpRecord, pInput), tmpRecord[tmpIdentity], false, false));
				}
			}
			tmpJQuery(tmpElementID).val(String(pValue)).trigger('change.select2');
		}
		catch (pError)
		{
			this.pict.log.error(`MaterialsEntitySelector[${this._getEntity(pInput)}].onTabularRowReorderRepaintTabular error: ${pError.message || pError}`);
		}
		return true;
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} [pRowIndex] - The row index parameter.
	 * @return {Promise<boolean>}
	 */
	async _onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pRowIndex)
	{
		try
		{
			const inputMethod = (pRowIndex || pRowIndex === 0) ? 'getTabularSelectDropdownID' : 'getSelectInputHTMLID';
			const tmpInputSelectElements = this.pict.ContentAssignment.getElement(this[inputMethod](pInput.Macro.RawHTMLID, pRowIndex));
			if (!tmpInputSelectElements || tmpInputSelectElements.length < 1)
			{
				return false;
			}
			let tmpCurrentSelection = this._safeSelect2Data(this[inputMethod](pInput.Macro.RawHTMLID, pRowIndex));
			let tmpValues = (pValue == null || pValue == '') ? [] : [ pValue ];
			let tmpNeedsClear = false;
			if (pInput.PictForm?.Multiple == true && typeof pValue === 'string')
			{
				tmpValues = pValue.split(',').map((v) => v.trim()).filter((v) => v.length > 0);
				if (Array.isArray(tmpCurrentSelection) && tmpCurrentSelection.length == tmpValues.length && tmpValues.every((v) => tmpCurrentSelection.find((s) => s.id == v)))
				{
					return;
				}
				tmpNeedsClear = true;
			}
			const tmpEntities = [];
			const tmpMissingValues = [];
			if (pInput.PictForm.SelectOptionsPickList)
			{
				tmpEntities.push(...this.pict.providers.DynamicMetaLists.getList(pInput.PictForm.SelectOptionsPickList, { extractRootKey: tmpValues }));
				for (const tmpEntity of tmpEntities)
				{
					tmpEntity.__Value = tmpEntity.id;
				}
			}
			else
			{
				for (const tmpValue of tmpValues)
				{
					// System 1: render single-select pickers from the local snapshot first; only load when missing.
					if (tmpValue && tmpValue !== 'null' && tmpValue !== NEW_ENTITY_ID) //FIXME: what's up with this null string?
					{
						// Prefer the local __Record (persisted snapshot / freshly synthesized record) — synchronous, no
						// fetch. No snapshot -> single-select defers to async backfill; multi-select fetches inline.
						let tmpEntity = this._getLocalRecordForValue(pView, pInput, tmpValue, pRowIndex);
						if (!tmpEntity)
						{
							if (!pInput.PictForm?.Multiple)
							{
								tmpMissingValues.push(tmpValue);
								continue;
							}
							try
							{
								const tmpEntityDisplay = await this.pict.providers.Select2Util.getEntityWithDisplayAsync(this._getEntity(pInput), tmpValue, this._buildEntityFetchOptions(pInput));
								tmpEntity = Object.assign({}, tmpEntityDisplay.Record, { __DisplayName: tmpEntityDisplay.DisplayName });
							}
							catch (pFetchError)
							{
								tmpEntity = null;
							}
						}
						if (tmpEntity && typeof tmpEntity === 'object' && tmpEntity[this._getRecordIdentity(pInput)])
						{
							tmpEntity.__Value = tmpValue;
							tmpEntities.push(tmpEntity);
						}
					}
				}
			}
			if (tmpNeedsClear)
			{
				// Drop only the options that are no longer selected, rather than emptying the
				// element and letting _renderEntitiesToPicker rebuild it. select2 repaints from
				// the element, so a full empty makes every tag vanish and come back -- and the
				// marshal fan-out runs this ~4x per field per change, so the user sees it.
				// Measured on LADOTD Sample Creation (one test removed): 8 empties across the
				// Tests and Labs pickers, both blanking mid-change.
				//
				// This is the second half of the flicker. The first was the ungated placeholder
				// strip in initializeSelect2; both had to go. _renderEntitiesToPicker below
				// already appends only what is missing, so pruning here leaves the picker
				// converging on the right set without ever being empty.
				this._pruneUnselectedOptions(this[inputMethod](pInput.Macro.RawHTMLID, pRowIndex), tmpValues);
				tmpCurrentSelection = this._safeSelect2Data(this[inputMethod](pInput.Macro.RawHTMLID, pRowIndex));
			}
			// _renderEntitiesToPicker only APPENDS selected options; a single-select clear resolves nothing to
			// render, so reset the picker explicitly. Guarded to a genuine clear (not a value pending backfill
			// or a new-record pin). Multi-select clears via the tmpNeedsClear path above.
			if (!pInput.PictForm?.Multiple && tmpEntities.length === 0 && tmpMissingValues.length === 0 && !tmpValues.includes(NEW_ENTITY_ID) && tmpCurrentSelection.length > 0)
			{
				window['jQuery'](this[inputMethod](pInput.Macro.RawHTMLID, pRowIndex)).val(null).trigger('change.select2');
			}
			this._renderEntitiesToPicker(pInput, tmpEntities, pRowIndex);
			this._renderDesyncAffordance(pView, pInput, pRowIndex);
			// System 2: load any values that had no local snapshot in the background and update the UI when they
			// arrive — non-blocking, so the user is in the form immediately on whatever System 1 rendered.
			if (tmpMissingValues.length > 0)
			{
				this._backfillMissingRecords(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pRowIndex, tmpMissingValues).catch((pError) =>
				{
					this.pict.log.warn(`MaterialsEntitySelector[${this._getEntity(pInput)}]: record backfill failed: ${pError.message || pError}`);
				});
			}
		}
		catch (pError)
		{
			this.pict.log.error(`Error in MaterialsEntitySelector[${this._getEntity(pInput)}]._onDataMarshalToForm: ${pError.message || pError}`);
		}
	}

	/**
	 * Build the entity-fetch options object (DB display fetch / backfill share it).
	 * @param {Object} pInput
	 * @return {Object}
	 */
	_buildEntityFetchOptions(pInput)
	{
		return {
			IdentityField: this._getRecordIdentity(pInput),
			JoinEntity: pInput.PictForm.JoinEntity,
			JoinEntityFirst: pInput.PictForm.JoinEntityFirst ?? true,
			JoinField: pInput.PictForm.JoinField ? pInput.PictForm.JoinField : pInput.PictForm.JoinEntity ? `ID${ pInput.PictForm.JoinEntity }` : null,
			EntityDisplayField: pInput.PictForm.EntityDisplayField,
			JoinEntityDisplayField: pInput.PictForm.JoinEntityDisplayField,
			Disabled: pInput.PictForm.Disabled || false,
			EntityTagLast: pInput.PictForm.EntityTag && pInput.PictForm.EntityTagLast,
		};
	}

	/**
	 * System 1 resolver: the locally-held record for a value — the persisted __Record snapshot or a freshly
	 * synthesized record, when its identity matches the value. Returns a shallow copy (so the transient marshal
	 * __Value never lands on the persisted snapshot), or null when there is no local record (System 2 must load).
	 * @param {Object} pView
	 * @param {Object} pInput
	 * @param {any} pValue
	 * @param {number|string} [pRowIndex]
	 * @return {Record<string, any>|null}
	 */
	_getLocalRecordForValue(pView, pInput, pValue, pRowIndex)
	{
		const tmpLocalRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), this._getRecordAddress(pView, pInput, pRowIndex));
		if (tmpLocalRecord && typeof tmpLocalRecord === 'object' && !Array.isArray(tmpLocalRecord))
		{
			const tmpIdentity = this._getRecordIdentity(pInput);
			if (tmpLocalRecord[tmpIdentity] == pValue)
			{
				return Object.assign({}, tmpLocalRecord);
			}
			// A synthesized/unsaved record carries its client identity (e.g. a GUID) but no server ID.
			// This is for the record creation path; retain the local record in this case.
			if (tmpLocalRecord[tmpIdentity] && !tmpLocalRecord[`ID${this._getEntity(pInput)}`])
			{
				return Object.assign({}, tmpLocalRecord);
			}
		}
		return null;
	}

	/**
	 * Append the given resolved records as selected options on the picker and repaint select2. Shared by the
	 * synchronous marshal (System 1) and the async backfill (System 2). Dedupes against the live selection.
	 * @param {Object} pInput
	 * @param {Array<Record<string, any>>} pEntities - each carrying the transient __Value (the option id) and, when resolved from a fetch, __DisplayName
	 * @param {number|string} [pRowIndex]
	 */
	_renderEntitiesToPicker(pInput, pEntities, pRowIndex)
	{
		const inputMethod = (pRowIndex || pRowIndex === 0) ? 'getTabularSelectDropdownID' : 'getSelectInputHTMLID';
		const tmpCurrentSelection = this._safeSelect2Data(this[inputMethod](pInput.Macro.RawHTMLID, pRowIndex));
		let tmpChanged = false;
		for (const tmpEntity of pEntities)
		{
			if (!tmpCurrentSelection.find((s) => s.id == tmpEntity.__Value))
			{
				let tmpOption;
				if (pInput.PictForm.SelectOptionsPickList)
				{
					tmpOption = new Option(tmpEntity.text, tmpEntity.id, false, true);
				}
				else
				{
					tmpOption = new Option(this._formatRecordForDisplay(tmpEntity, pInput), tmpEntity[this._getRecordIdentity(pInput)], false, true);
				}
				tmpChanged = true;
				window['jQuery'](this[inputMethod](pInput.Macro.RawHTMLID, pRowIndex)).append(tmpOption);
			}
			delete tmpEntity.__Value;
			delete tmpEntity.__DisplayName;
		}
		if (tmpChanged)
		{
			window['jQuery'](this[inputMethod](pInput.Macro.RawHTMLID, pRowIndex)).trigger('change.select2');
		}
	}

	/**
	 * System 2: asynchronously load records that had no local snapshot, persist single-select results at
	 * __Record, and render them — non-blocking. Keeps any existing __Record on failure (offline-tolerant).
	 * @param {Object} pView @param {Object} pGroup @param {Object} pRow @param {Object} pInput
	 * @param {any} pValue @param {string} pHTMLSelector @param {number|string} [pRowIndex] @param {Array<any>} [pValues]
	 */
	async _backfillMissingRecords(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pRowIndex, pValues = [])
	{
		const tmpLoaded = [];
		for (const tmpValue of pValues)
		{
			let tmpEntity = null;
			let tmpDisplayName = null;
			try
			{
				const tmpEntityDisplay = await this.pict.providers.Select2Util.getEntityWithDisplayAsync(this._getEntity(pInput), tmpValue, this._buildEntityFetchOptions(pInput));
				tmpEntity = tmpEntityDisplay.Record;
				tmpDisplayName = tmpEntityDisplay.DisplayName;
			}
			catch (pFetchError)
			{
				this.pict.log.warn(`MaterialsEntitySelector[${this._getEntity(pInput)}]: backfill load failed for [${tmpValue}]: ${pFetchError.message || pFetchError}`);
			}
			if (tmpEntity && typeof tmpEntity === 'object' && tmpEntity[this._getRecordIdentity(pInput)])
			{
				if (!pInput.PictForm?.Multiple)
				{
					this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), this._getRecordAddress(pView, pInput, pRowIndex), tmpEntity);
				}
				const tmpForRender = Object.assign({}, tmpEntity);
				tmpForRender.__Value = tmpValue;
				tmpForRender.__DisplayName = tmpDisplayName;
				tmpLoaded.push(tmpForRender);
			}
		}
		if (tmpLoaded.length > 0)
		{
			this._renderEntitiesToPicker(pInput, tmpLoaded, pRowIndex);
		}
	}

	/**
	 * Pure decision for the de-sync state of a snapshot vs the server record.
	 * @param {Record<string, any>} pSnapshot - the locally-held __Record snapshot
	 * @param {any} pFetched - the freshly-fetched server record (or null/empty if not found)
	 * @param {string} pIdentityField - the record identity field (e.g. GUIDSample)
	 * @return {'stale'|'deleted'|null}
	 */
	_computeDesyncState(pSnapshot, pFetched, pIdentityField)
	{
		if (!pFetched || typeof pFetched !== 'object' || Array.isArray(pFetched) || !pFetched[pIdentityField])
		{
			return 'deleted';
		}
		if (pSnapshot && pSnapshot.UpdateDate && pFetched.UpdateDate && pSnapshot.UpdateDate !== pFetched.UpdateDate)
		{
			return 'stale';
		}
		return null;
	}

	_desyncKey(pInput, pRowIndex)
	{
		return `${pInput.Macro.RawHTMLID}::${(pRowIndex || pRowIndex === 0) ? pRowIndex : '_'}`;
	}

	_desyncState()
	{
		if (!this._recordDesyncState)
		{
			this._recordDesyncState = {};
			this._recordDesyncFetched = {};
		}
		return this._recordDesyncState;
	}

	/**
	 * System 2 de-sync check (opt-in via PictForm.DetectDesync). Asynchronously compares the rendered snapshot
	 * __Record against the server and flags 'stale'/'deleted' without mutating the snapshot. Non-blocking; never
	 * runs on a synthesized (unsaved) record, a multi-select picker, or a frozen document.
	 * @param {Object} pView @param {Object} pInput @param {number} [pRowIndex]
	 */
	async _checkRecordDesync(pView, pInput, pRowIndex)
	{
		if (!pInput.PictForm?.DetectDesync || pInput.PictForm?.Multiple || this._isRecordFrozen())
		{
			return;
		}
		const tmpSnapshot = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), this._getRecordAddress(pView, pInput, pRowIndex));
		if (!tmpSnapshot || typeof tmpSnapshot !== 'object' || Array.isArray(tmpSnapshot))
		{
			return;
		}
		const tmpIdentity = tmpSnapshot[this._getRecordIdentity(pInput)];
		// Only a saved record (has a DB id) is comparable; a synthesized record has nothing on the server yet.
		if (!tmpIdentity || !tmpSnapshot[`ID${this._getEntity(pInput)}`])
		{
			return;
		}
		let tmpFetched = null;
		try
		{
			tmpFetched = await this.pict.providers.Select2Util.getEntityAsync(this._getEntity(pInput), tmpIdentity, Object.assign({ }, this._buildEntityFetchOptions(pInput), { BypassCache: true }));
		}
		catch (pError)
		{
			// Couldn't reach the server (offline): leave the snapshot as-is, raise no false de-sync flag.
			return;
		}
		const tmpKey = this._desyncKey(pInput, pRowIndex);
		const tmpState = this._computeDesyncState(tmpSnapshot, tmpFetched, this._getRecordIdentity(pInput));
		this._desyncState()[tmpKey] = tmpState;
		if (tmpState === 'stale')
		{
			this._recordDesyncFetched[tmpKey] = tmpFetched;
		}
		this._renderDesyncAffordance(pView, pInput, pRowIndex);
	}

	/**
	 * Inject/update the "out of date / no longer exists" affordance next to the picker, reflecting the
	 * transient de-sync state. Clicking it runs the manual refresh. No-op outside a browser.
	 * @param {Object} pView @param {Object} pInput @param {number} [pRowIndex]
	 */
	_renderDesyncAffordance(pView, pInput, pRowIndex)
	{
		if (typeof document === 'undefined' || !window['jQuery'])
		{
			return;
		}
		const tmpMethod = (pRowIndex || pRowIndex === 0) ? 'getTabularSelectDropdownID' : 'getSelectInputHTMLID';
		const tmpSelectID = this[tmpMethod](pInput.Macro.RawHTMLID, pRowIndex);
		const tmpElements = this.pict.ContentAssignment.getElement(tmpSelectID);
		const tmpSelectElement = tmpElements && tmpElements[0];
		if (!tmpSelectElement || !tmpSelectElement.parentElement)
		{
			return;
		}
		const tmpState = this._desyncState()[this._desyncKey(pInput, pRowIndex)];
		const tmpAffordanceID = `${tmpSelectID}--desync`.replace(/[^A-Za-z0-9_-]/g, '');
		let tmpAffordance = document.getElementById(tmpAffordanceID);
		if (!tmpState)
		{
			if (tmpAffordance)
			{
				tmpAffordance.remove();
			}
			return;
		}
		if (!tmpAffordance)
		{
			tmpAffordance = document.createElement('a');
			tmpAffordance.id = tmpAffordanceID;
			tmpAffordance.className = 'materials-record-desync is-size-7 has-text-warning';
			tmpAffordance.style.cssText = 'display:block;cursor:pointer;margin-top:2px;';
			tmpAffordance.addEventListener('click', (pEvent) =>
			{
				pEvent.preventDefault();
				this._refreshRecordManually(pView, pInput, pRowIndex);
			});
			tmpSelectElement.parentElement.appendChild(tmpAffordance);
		}
		tmpAffordance.textContent = tmpState === 'deleted' ? '⚠ This record no longer exists — dismiss' : '↻ Out of date — refresh';
	}

	/**
	 * Manual refresh: apply the freshly-fetched record as the new snapshot and re-sync the autofilled sibling
	 * fields (via _onRecordRefresh), then clear the de-sync flag. A 'deleted' record is simply dismissed,
	 * keeping the last-known snapshot.
	 * @param {Object} pView @param {Object} pInput @param {number} [pRowIndex]
	 */
	_refreshRecordManually(pView, pInput, pRowIndex)
	{
		const tmpKey = this._desyncKey(pInput, pRowIndex);
		if (this._desyncState()[tmpKey] === 'stale')
		{
			const tmpFetched = this._recordDesyncFetched[tmpKey];
			if (tmpFetched)
			{
				const tmpAddress = this._getRecordAddress(pView, pInput, pRowIndex);
				this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpAddress, tmpFetched);
				this._onRecordRefresh(pView, pInput, tmpFetched, tmpAddress, this.pict.getUUID(), pRowIndex);
				const tmpEntity = this._getEntity(pInput);
				const tmpGUID = tmpFetched[`GUID${tmpEntity}`];
				if (this._isScopedEntityDataEnabled() && typeof tmpGUID === 'string' && tmpGUID.length > 0)
				{
					EntityDataBaseline.refreshBaselineRecords(this.pict, tmpEntity, [ tmpGUID ]);
				}
			}
		}
		delete this._recordDesyncFetched[tmpKey];
		this._desyncState()[tmpKey] = null;
		this._renderDesyncAffordance(pView, pInput, pRowIndex);
		pView.marshalToView();
	}

	/**
	 * @param {Record<string, any>} pRecord
	 * @param {Object} pInput
	 * @param {boolean} [pUseEntityTag=false]
	 *
	 * @return {string}
	 */
	_formatRecordForDisplay(pRecord, pInput, pUseEntityTag = true)
	{
		const tmpDisplayName = this._getRecordDisplayName(pRecord, pInput);
		// if the EntityTag is defined and the entity has that property, use it for the leading tag in the selection display
		if (pUseEntityTag && pInput.PictForm.EntityTag)
		{
			const tmpTag = pRecord[pInput.PictForm.EntityTag];
			// Suppress the leading tag when it's empty or identical to the name (e.g. Labs whose code IS their
			// name) — it would otherwise render a redundant "(X) X" pill (and the PDF shows only the name).
			if (tmpTag != null && String(tmpTag).trim() !== '' && String(tmpTag).trim() !== String(tmpDisplayName).trim())
			{
				return `${tmpTag}|~|${tmpDisplayName}`;
			}
		}
		return tmpDisplayName;
	}

	/**
	 * @param {Record<string, any>} pRecord
	 * @param {Object} pInput
	 *
	 * @return {string}
	 */
	_getRecordDisplayName(pRecord, pInput)
	{
		// if an EntityDisplayField is defined, use it
		if (pInput.PictForm.EntityDisplayField && pRecord[pInput.PictForm.EntityDisplayField])
		{
			return pRecord[pInput.PictForm.EntityDisplayField];
		}
		// if a EntityDisplayField is defined with dot notation, use it's child field
		else if (pInput.PictForm.EntityDisplayField && pInput.PictForm.EntityDisplayField.includes('.'))
		{
			const tmpJoinParts = pInput.PictForm.EntityDisplayField.split('.');
			if (tmpJoinParts.length === 2 && pRecord[tmpJoinParts[0]] && pRecord[tmpJoinParts[0]][tmpJoinParts[1]])
			{
				return pRecord[tmpJoinParts[0]][tmpJoinParts[1]];
			}
		}
		return pRecord.__DisplayName || pRecord.DisplayName || pRecord.Name || (`Unnamed ${this._getEntity(pInput)} #` + pRecord[`ID${this._getEntity(pInput)}`]);
	}

	/**
	 * Handles the data request event for a select input in the PictProviderInputSelect class.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value object.
	 * @param {string} pHTMLSelector - The HTML selector object.
	 * @return {any} - The result of the onDataRequest method.
	 */
	onDataRequest(pView, pInput, pValue, pHTMLSelector)
	{
		const tmpInputSelectValue = this.pict.ContentAssignment.readContent(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID));
		this.pict.ContentAssignment.assignContent(pHTMLSelector, tmpInputSelectValue);
		pView.dataChanged(pInput.Hash);
		return super.onDataRequest(pView, pInput, tmpInputSelectValue, pHTMLSelector);
	}

	/**
	 * Handles the data request event for a tabular input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value object.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The row index.
	 * @return {any} - The result of the data request.
	 */
	onDataRequestTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex)
	{
		const tmpInputSelectValue = this.pict.ContentAssignment.readContent(this.getTabularSelectDropdownID(pInput.Macro.RawHTMLID, pRowIndex));
		this.pict.ContentAssignment.assignContent(this.getTabularSelectInputID(pInput.Macro.RawHTMLID, pRowIndex), tmpInputSelectValue);
		pView.dataChangedTabular(pInput.PictForm.GroupIndex, pInput.PictForm.InputIndex, pRowIndex);
		return super.onDataRequestTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex);
	}

	/**
	 * Handles the data request event for a select input in the PictProviderInputSelect class.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value object.
	 * @param {string} pHTMLSelector - The HTML selector object.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {any} - The result of the onDataChange method.
	 */
	onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this._handleSelectionRemovals(pView, pInput, pValue, null);
		pValue = this._coerceClearedEntityValue(pView, pInput, pValue, null);
		let tmpNewEntity = false;
		if (pValue === NEW_ENTITY_ID)
		{
			this._generateSaltIfNeeded(pView, pInput, null, true);
			pValue = this._handleDefaultRecord(pView, pInput, pValue, pTransactionGUID);
			tmpNewEntity = true;
		}
		this._refreshRecord(pView, pInput, pValue, this._getEntity(pInput), pTransactionGUID, this._getRecordIdentity(pInput), null, (pRecord) =>
		{
			if (!pRecord && tmpNewEntity)
			{
				const tmpRecordAddress = this._getRecordAddress(pView, pInput);
				const tmpRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpRecordAddress);
				if (tmpRecord)
				{
					this._onRecordRefresh(pView, pInput, tmpRecord, this._getRecordAddress(pView, pInput), pTransactionGUID);
				}
			}
		});
		return super.onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Cross-field dependency cleanup. When an anchor field this picker depends on
	 * (declared via `PictForm.DynamicFilters[].TriggerGroupHash`) changes, the
	 * value already selected here may no longer belong to the new anchor — so
	 * clear it. The anchor emits the group through Pict-Input-AutofillTriggerGroup
	 * (`TriggerAllInputs: true`); this handler receives it like every other input.
	 *
	 * @param {Object} pView
	 * @param {Object} pInput
	 * @param {any} pValue
	 * @param {string} pHTMLSelector
	 * @param {string} pEvent - global event payload, e.g. `TriggerGroup:<hash>:DataChange:<emitter>:<uuid>`
	 * @param {string} pTransactionGUID
	 * @return {any}
	 */
	onAfterEventCompletion(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID)
	{
		const tmpMatchedFilter = this._matchedDynamicFilterForEvent(pInput, pEvent);
		if (tmpMatchedFilter && (!pInput.PictForm?.SmartClear || this._isSelectionIllegalForFilter(pView, pInput, tmpMatchedFilter, null)))
		{
			this._clearForDynamicFilter(pView, pInput, null);
		}
		// Autofill scope vetting (opt-in via PictForm.VetAutofillScope): when an autofill from a non-projection
		// source lands a value illegal for THIS picker's own Through filter, suppress it. Async — the Through
		// slice primes on demand, so it runs after the sync autofill has landed the value. Fail-open throughout.
		if (pInput.PictForm?.VetAutofillScope && !this.pict._MaterialsEntityHydrationDepth && this._matchedAutofillGroupForEvent(pInput, pEvent))
		{
			this._vetAutofilledValueAgainstFilters(pView, pInput, null).catch((pError) =>
			{
				this.pict.log.error(`Autofill scope vet failed (value kept): ${pError?.message || pError}`);
			});
		}
		// Warm any query-strategy Through slice for the new anchor value so a
		// dependent picker's INN clause resolves synchronously once it is opened.
		this.pict.providers.ThroughScopeResolver?.primeQueryScopesFromForm?.();
		// Re-prime lab scoping — the project or the selected-test set may have changed. Idempotent: only re-fetches
		// (ProjectLabJoin + batched TestLabJoin) when the (project, test-set) key actually changes.
		this.pict.providers.LabScopeResolver?.primeFromForm?.();
		this._handleBaseSaveProjectionEvent(pView, pInput, null, pEvent, pTransactionGUID);
		return super.onAfterEventCompletion(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID);
	}

	/**
	 * Pure: does a global event target one of this input's DynamicFilters?
	 * Returns the matching filter entry, or null. Self-emitted events are ignored.
	 *
	 * @param {Object} pInput
	 * @param {string} pEvent
	 * @return {object|null}
	 */
	_matchedDynamicFilterForEvent(pInput, pEvent)
	{
		const tmpDynamicFilters = pInput && pInput.PictForm ? pInput.PictForm.DynamicFilters : null;
		if (!Array.isArray(tmpDynamicFilters))
		{
			return null;
		}
		const tmpParts = (typeof pEvent === 'string' ? pEvent : '').split(':');
		if (tmpParts[0] !== 'TriggerGroup')
		{
			return null;
		}
		const tmpGroupHash = tmpParts[1];
		const tmpEmitterHash = tmpParts[3];
		if ((pInput.Hash || pInput.DataAddress) === tmpEmitterHash)
		{
			return null;
		}
		return tmpDynamicFilters.find((pFilter) => pFilter && pFilter.TriggerGroupHash === tmpGroupHash) || null;
	}

	/**
	 * Tabular sibling of `_matchedDynamicFilterForEvent`. A tabular anchor fires a
	 * ROW-SCOPED event (`TriggerGroup:<hash>:RecordRefresh:<emitter>:<rowIndex>:<uuid>`,
	 * see _onRecordRefresh). The global broadcast delivers it to EVERY row's
	 * onAfterEventTabularCompletion, so a dependent column must act on its own row
	 * ONLY — otherwise changing one row's Source would wipe valid selections in every
	 * other row. We therefore require the event's row index (slot [4]) to equal this
	 * picker's `pRowIndex`. A global (non-row-scoped) event has a UUID in slot [4],
	 * which `parseInt` yields NaN for, so it never matches here.
	 *
	 * @param {Object} pInput
	 * @param {string} pEvent
	 * @param {number} pRowIndex - this picker's row
	 * @return {object|null}
	 */
	_matchedDynamicFilterForEventTabular(pInput, pEvent, pRowIndex)
	{
		const tmpDynamicFilters = pInput && pInput.PictForm ? pInput.PictForm.DynamicFilters : null;
		if (!Array.isArray(tmpDynamicFilters))
		{
			return null;
		}
		const tmpParts = (typeof pEvent === 'string' ? pEvent : '').split(':');
		if (tmpParts[0] !== 'TriggerGroup')
		{
			return null;
		}
		const tmpGroupHash = tmpParts[1];
		const tmpEmitterHash = tmpParts[3];
		const tmpEventRowIndex = parseInt(tmpParts[4], 10);
		if ((pInput.Hash || pInput.DataAddress) === tmpEmitterHash)
		{
			return null;
		}
		// Only row-scoped events carry an integer row index here; require it to be OUR row.
		if (isNaN(tmpEventRowIndex) || tmpEventRowIndex !== Number(pRowIndex))
		{
			return null;
		}
		return tmpDynamicFilters.find((pFilter) => pFilter && pFilter.TriggerGroupHash === tmpGroupHash) || null;
	}

	/**
	 * Tabular sibling of onAfterEventCompletion: when an anchor in THIS row changes,
	 * clear this row's now-orphaned selection so it can't keep a value that no longer
	 * belongs to the new anchor. The next dropdown open re-fetches with the updated
	 * per-row FromRowField filter.
	 *
	 * @param {Object} pView
	 * @param {Object} pInput
	 * @param {any} pValue
	 * @param {string} pHTMLSelector
	 * @param {number} pRowIndex
	 * @param {string} pEvent
	 * @param {string} pTransactionGUID
	 * @return {any}
	 */
	onAfterEventTabularCompletion(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID)
	{
		const tmpMatchedFilter = this._matchedDynamicFilterForEventTabular(pInput, pEvent, pRowIndex);
		if (tmpMatchedFilter && (!pInput.PictForm?.SmartClear || this._isSelectionIllegalForFilter(pView, pInput, tmpMatchedFilter, pRowIndex)))
		{
			this._clearForDynamicFilter(pView, pInput, pRowIndex);
		}
		// Autofill scope vetting (opt-in) — tabular sibling; vet only this row's autofilled value. Async / fail-open.
		if (pInput.PictForm?.VetAutofillScope && !this.pict._MaterialsEntityHydrationDepth && this._matchedAutofillGroupForEvent(pInput, pEvent))
		{
			this._vetAutofilledValueAgainstFilters(pView, pInput, pRowIndex).catch((pError) =>
			{
				this.pict.log.error(`Autofill scope vet failed (value kept): ${pError?.message || pError}`);
			});
		}
		this._handleBaseSaveProjectionEvent(pView, pInput, pRowIndex, pEvent, pTransactionGUID);
		return super.onAfterEventTabularCompletion(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID);
	}

	/**
	 * Smart-clear gate (opt-in via `PictForm.SmartClear`). Returns true only when this picker's resolved
	 * record is DEFINITIVELY illegal for the changed anchor — the record carries the filter's `Field` and
	 * its value no longer equals the anchor's current value. When the record isn't resolved yet, lacks the
	 * FK, or the anchor is unset, illegality can't be proven, so we keep the value: this avoids clobbering a
	 * just-autofilled selection during sample projection, and preserves a user override that sits outside
	 * the filter. (Without this, plain always-clear on the anchor's trigger group would wipe a Product/
	 * MixDesign that a sample selection is concurrently projecting.)
	 *
	 * @param {Object} pView
	 * @param {Object} pInput
	 * @param {object} pFilter - the matched DynamicFilters entry (`{ Field, FromField | FromRowField, ... }`).
	 * @param {number|null} [pRowIndex]
	 * @return {boolean} true only when the current selection is provably illegal for pFilter.
	 */
	_isSelectionIllegalForFilter(pView, pInput, pFilter, pRowIndex)
	{
		if (!pFilter || !pFilter.Field)
		{
			return false;
		}
		const tmpRecordAddress = this._getRecordAddress(pView, pInput, pRowIndex);
		const tmpRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpRecordAddress);
		if (!tmpRecord || typeof tmpRecord !== 'object' || Array.isArray(tmpRecord) || !(pFilter.Field in tmpRecord))
		{
			return false;
		}
		let tmpAnchorValue;
		if (pFilter.FromRowField && pRowIndex != null && pInput.PictForm && pInput.PictForm.InformaryContainerAddress)
		{
			const tmpRowAddress = this.pict.providers.Informary.getComposedContainerAddress(pInput.PictForm.InformaryContainerAddress, pRowIndex, pFilter.FromRowField);
			tmpAnchorValue = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpRowAddress);
		}
		else if (pFilter.FromField)
		{
			tmpAnchorValue = this.pict.manifest.getValueByHash(this.pict, pFilter.FromField);
		}
		else
		{
			return false;
		}
		// An unset anchor imposes no constraint (applyDynamicFilters omits it), so nothing is illegal.
		const tmpAnchorNormalized = (tmpAnchorValue === null || tmpAnchorValue === undefined) ? '' : String(tmpAnchorValue).trim();
		if (tmpAnchorNormalized === '' || tmpAnchorNormalized === '0')
		{
			return false;
		}
		return String(tmpRecord[pFilter.Field]) !== tmpAnchorNormalized;
	}

	/**
	 * Pure: does a global event correspond to an AUTOFILL into this input (an AutofillTriggerGroup entry whose
	 * TriggerGroupHash matches the event)? Returns the matched entry, or null. Self-emitted events, and entries
	 * flagged `AllowInvalidProjection`, are skipped — the latter marks record-projection sources (e.g. linking a
	 * saved sample) that legitimately carry pre-existing, possibly out-of-scope values that must not be vetted.
	 *
	 * @param {Object} pInput
	 * @param {string} pEvent
	 * @return {object|null}
	 */
	_matchedAutofillGroupForEvent(pInput, pEvent)
	{
		let tmpGroups = (pInput && pInput.PictForm) ? pInput.PictForm.AutofillTriggerGroup : null;
		if (!tmpGroups)
		{
			return null;
		}
		if (!Array.isArray(tmpGroups))
		{
			tmpGroups = [ tmpGroups ];
		}
		const tmpParts = (typeof pEvent === 'string' ? pEvent : '').split(':');
		if (tmpParts[0] !== 'TriggerGroup')
		{
			return null;
		}
		const tmpGroupHash = tmpParts[1];
		const tmpEmitterHash = tmpParts[3];
		if ((pInput.Hash || pInput.DataAddress) === tmpEmitterHash)
		{
			return null;
		}
		return tmpGroups.find((pGroup) => pGroup && pGroup.TriggerGroupHash === tmpGroupHash && !pGroup.AllowInvalidProjection) || null;
	}

	/**
	 * Vet this picker's current value against its own Through DynamicFilters and suppress it when it is
	 * PROVABLY not a legal option — the selected `ThroughSelect` id is absent from the resolved allowed set for
	 * the current anchor. Fail-open everywhere else: no anchor, an unprimed/unresolved scope (getThroughIDs
	 * null), or no value all KEEP the value. Async because query-strategy Through slices load on demand — which
	 * also means it runs after the synchronous autofill has landed the value. Rejects an autofill (e.g.
	 * Plant → Producer) that lands an org outside the material-scoped supplier set; a plain user pick is always
	 * in-scope, and record-projection sources are excluded upstream via `_matchedAutofillGroupForEvent`.
	 *
	 * @param {Object} pView @param {Object} pInput @param {number|null} [pRowIndex]
	 * @return {Promise<void>}
	 */
	async _vetAutofilledValueAgainstFilters(pView, pInput, pRowIndex)
	{
		const tmpFilters = (pInput && pInput.PictForm) ? pInput.PictForm.DynamicFilters : null;
		const tmpResolver = this.pict.providers.ThroughScopeResolver;
		if (!Array.isArray(tmpFilters) || !tmpResolver)
		{
			return;
		}
		const tmpThroughFilters = tmpFilters.filter((pFilter) => pFilter && pFilter.Through && pFilter.ThroughSelect && pFilter.FromField);
		if (!tmpThroughFilters.length)
		{
			return;
		}
		const tmpDestination = pView.getMarshalDestinationObject();
		for (const tmpFilter of tmpThroughFilters)
		{
			const tmpAnchor = this.pict.manifest.getValueByHash(this.pict, tmpFilter.FromField);
			const tmpAnchorNormalized = (tmpAnchor === null || tmpAnchor === undefined) ? '' : String(tmpAnchor).trim();
			if (tmpAnchorNormalized === '' || tmpAnchorNormalized === '0')
			{
				continue;
			}
			const tmpRecord = this.pict.manifest.getValueAtAddress(tmpDestination, this._getRecordAddress(pView, pInput, pRowIndex));
			let tmpSelected = (tmpRecord && typeof tmpRecord === 'object' && !Array.isArray(tmpRecord)) ? tmpRecord[tmpFilter.ThroughSelect] : null;
			if ((tmpSelected === null || tmpSelected === undefined || tmpSelected === '') && pRowIndex == null)
			{
				tmpSelected = this.pict.manifest.getValueAtAddress(tmpDestination, pInput.DataAddress);
			}
			if (tmpSelected === null || tmpSelected === undefined || tmpSelected === '' || Number(tmpSelected) === 0)
			{
				continue;
			}
			await tmpResolver.primeThroughSlice(tmpFilter, tmpAnchor);
			const tmpAllowed = tmpResolver.getThroughIDs(tmpFilter, tmpAnchor);
			// Fail-open backstop: an empty allowed set means the scope couldn't be resolved (sparse/missing
			// OrganizationMaterialJoin data, or a failed prime) — treat that as "can't constrain", not "everything
			// is illegal". Clearing on an empty set would nuke a legitimately-chosen value. A genuinely-scoped
			// anchor returns a non-empty set and still clears an out-of-scope value below.
			if (!tmpAllowed || typeof tmpAllowed.has !== 'function' || tmpAllowed.size === 0)
			{
				continue;
			}
			if (!tmpAllowed.has(Number(tmpSelected)))
			{
				this._clearForDynamicFilter(pView, pInput, pRowIndex);
				return;
			}
		}
	}

	/**
	 * Reset this picker's value and its resolved-record companion (`__Record`),
	 * then re-marshal so the select2 reflects the cleared state. `__Record` is
	 * reset to an empty object (not deleted) to match the selector convention —
	 * new-record flows write fields into it and assume it is always an object.
	 * The next dropdown open re-fetches with the updated DynamicFilters (the new
	 * anchor value), so neither a stale selection nor a stale resolved record
	 * survives an anchor change.
	 *
	 * @param {Object} pView
	 * @param {Object} pInput
	 * @param {number|null} [pRowIndex]
	 */
	_clearForDynamicFilter(pView, pInput, pRowIndex)
	{
		try
		{
			this._handleEntitySelectionCleared(pView, pInput, pRowIndex);
			const tmpDestination = pView.getMarshalDestinationObject();
			const tmpRecordAddress = this._getRecordAddress(pView, pInput, pRowIndex, '__Record');
			// Reset the value and its resolved-record companion. __Record is reset to
			// an EMPTY OBJECT (not undefined/deleted) to match the selector convention:
			// downstream flows write fields into __Record and assume it is always an
			// object (e.g. new-record creation in MaterialsSampleSelector, which sets
			// tmpRecord.Status on SampleStatusChanged).
			this.pict.manifest.setValueAtAddress(tmpDestination, tmpRecordAddress, {});
			if (pRowIndex != null)
			{
				// Tabular: clear ONLY this row's cell + its own select2 instance.
				pView.setDataTabularByHash(pInput.PictForm.GroupIndex, pInput.Hash, pRowIndex, pInput.Default ?? '');
				const tmpTabularSelectID = this.getTabularSelectDropdownID(pInput.Macro.RawHTMLID, pRowIndex);
				if (window['jQuery'] && window['jQuery'](tmpTabularSelectID).data('select2'))
				{
					window['jQuery'](tmpTabularSelectID).val(null).empty().trigger('change.select2');
				}
				pView.manualMarshalTabularDataToViewByInput(pInput, pRowIndex);
			}
			else
			{
				pView.setDataByInput(pInput, pInput.Default ?? '');
				const tmpSelectID = this.getSelectInputHTMLID(pInput.Macro.RawHTMLID);
				if (window['jQuery'] && window['jQuery'](tmpSelectID).data('select2'))
				{
					window['jQuery'](tmpSelectID).val(null).empty().trigger('change.select2');
				}
				pView.manualMarshalDataToViewByInput(pInput);
			}
			this.pict.log.info(`DynamicFilter: cleared dependent input [${pInput.Hash}]${pRowIndex != null ? ` row ${pRowIndex}` : ''} after its anchor changed.`);
		}
		catch (pError)
		{
			this.pict.log.error(`DynamicFilter: failed to clear dependent input [${pInput && pInput.Hash}]: ${pError}`);
		}
	}

	/**
	 * Handles data requests for the Pict-Provider-InputExtension.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value from AppData.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The row index of the tabular data.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {boolean} - Returns true.
	 */
	onDataChangeTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this._handleSelectionRemovals(pView, pInput, pValue, pRowIndex);
		pValue = this._coerceClearedEntityValue(pView, pInput, pValue, pRowIndex);
		let tmpNewEntity = false;
		if (pValue === NEW_ENTITY_ID)
		{
			this._generateSaltIfNeeded(pView, pInput, pRowIndex, true);
			pValue = this._handleDefaultRecord(pView, pInput, pValue, pTransactionGUID, pRowIndex);
			tmpNewEntity = true;
		}
		this._refreshRecord(pView, pInput, pValue, this._getEntity(pInput), pTransactionGUID, this._getRecordIdentity(pInput), pRowIndex, (pRecord) =>
		{
			if (!pRecord && tmpNewEntity)
			{
				const tmpRecordAddress = this._getRecordAddress(pView, pInput, pRowIndex);
				const tmpRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpRecordAddress);
				if (tmpRecord)
				{
					this._onRecordRefresh(pView, pInput, tmpRecord, this._getRecordAddress(pView, pInput), pTransactionGUID);
				}
			}
		});
		return super.onDataChangeTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	_translateRecordTemplateFields(pRecord)
	{
		for (const tmpField in pRecord)
		{
			if (typeof pRecord[tmpField] === 'string')
			{
				pRecord[tmpField] = this.pict.parseTemplate(pRecord[tmpField], this.pict);
			}
		}
	}

	/**
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {string|number} pIDValue
	 * @param {string} pEntity
	 * @param {string} pTransactionGUID - A transaction GUID to associate with this operation.
	 * @param {string} [pRecordIdentityField]
	 * @param {number} [pRowIndex] - The row index. If present, this is a tabular input.
	 * @param {(pRecord: Record<string, any>) => void} [fCallback] - (optional) A callback function to be called after the record is refreshed.
	 */
	/**
	 * Whether the document is frozen (read-only / Locked). A frozen document always uses the persisted
	 * snapshot — System 2 never refreshes it. Same lock signal the sibling selectors gate interactivity on.
	 * @return {boolean}
	 */
	_isRecordFrozen()
	{
		return !this.pict?.AppData?.Interactive || !!this.pict?.AppData?.DocumentData?.ReportData?.Locked;
	}

	/**
	 * Whether a record fetch has been overtaken: the input's committed value no longer names the ids the
	 * fetch asked for. A value nobody has written yet is no evidence either way, so it never counts as stale.
	 * @param {Object} pView @param {Object} pInput @param {number|string|null} pRowIndex
	 * @param {any} pRequestedValue - The value the fetch was issued for.
	 * @return {boolean}
	 */
	_isStaleRecordRefresh(pView, pInput, pRowIndex, pRequestedValue)
	{
		const tmpCurrentValue = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), this._getValueAddress(pView, pInput, pRowIndex));
		if (tmpCurrentValue === undefined || tmpCurrentValue === null)
		{
			return false;
		}
		const tmpCurrent = toIdentitySet(tmpCurrentValue);
		const tmpRequested = toIdentitySet(pRequestedValue);
		if (tmpCurrent.size !== tmpRequested.size)
		{
			return true;
		}
		for (const tmpIdentity of tmpRequested)
		{
			if (!tmpCurrent.has(tmpIdentity))
			{
				return true;
			}
		}
		return false;
	}

	/**
	 * Fetch the record(s) behind a value and cascade them. A response that arrives after the value has moved on
	 * is discarded without writing the record, cascading or calling fCallback; the async operation still completes.
	 * @param {Object} pView @param {Object} pInput @param {any} pIDValue @param {string} pEntity
	 * @param {string} pTransactionGUID @param {string} [pRecordIdentityField] @param {number|string|null} [pRowIndex]
	 * @param {(pRecord: any) => void} [fCallback]
	 */
	_refreshRecord(pView, pInput, pIDValue, pEntity, pTransactionGUID, pRecordIdentityField, pRowIndex, fCallback)
	{
		const tmpRefreshEvent = `${pEntity}Refresh`;
		pView.registerEventTransactionAsyncOperation(pTransactionGUID, tmpRefreshEvent);
		const tmpRecordAddress = this._getRecordAddress(pView, pInput, pRowIndex);
		if (!pIDValue)
		{
			// if we don't have an ID value, we should clear out any existing record at this address and fire the change event with an empty record
			// rather than go do a fetch with an empty or undefined ID which is wasteful
			const tmpRecordPayload = pInput.PictForm.Multiple ? [] : { };
			this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpRecordAddress, tmpRecordPayload);
			this._onRecordRefresh(pView, pInput, tmpRecordPayload, tmpRecordAddress, pTransactionGUID, pRowIndex);
			if (typeof fCallback === 'function')
			{
				fCallback(tmpRecordPayload);
			}
			pView.eventTransactionAsyncOperationComplete(pTransactionGUID, tmpRefreshEvent);
			return;
		}
		const tmpRecordIdentityField = pRecordIdentityField || `ID${pEntity}`;
		// Refresh by default: re-fetch the latest record on open. Two cases keep the persisted snapshot instead (when
		// it already matches the value): a frozen document (read-only / Locked), or a field that explicitly opts out
		// with PictForm.RefreshOnOpen === false. A changed selection never matches the snapshot, so it always loads.
		const tmpExistingRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpRecordAddress);
		if (!pInput.PictForm?.Multiple && tmpExistingRecord && typeof tmpExistingRecord === 'object' && !Array.isArray(tmpExistingRecord) &&
			tmpExistingRecord[tmpRecordIdentityField] == pIDValue && (this._isRecordFrozen() || pInput.PictForm?.RefreshOnOpen === false))
		{
			this._onRecordRefresh(pView, pInput, tmpExistingRecord, tmpRecordAddress, pTransactionGUID, pRowIndex);
			if (typeof fCallback === 'function')
			{
				fCallback(tmpExistingRecord);
			}
			pView.eventTransactionAsyncOperationComplete(pTransactionGUID, tmpRefreshEvent);
			return;
		}
		const fOnRecords = (pError, pRecords) =>
		{
			// The value moved on while this fetch was in flight; the refresh for the current value owns the record.
			if (this._isStaleRecordRefresh(pView, pInput, pRowIndex, pIDValue))
			{
				pView.eventTransactionAsyncOperationComplete(pTransactionGUID, tmpRefreshEvent);
				return;
			}
			if (pError || !Array.isArray(pRecords))
			{
				this.pict.log.error(`Error fetching ${pEntity} record with ${tmpRecordIdentityField} = ${pIDValue}: ${pError || 'No records found'}`);
				if (typeof fCallback === 'function')
				{
					fCallback(null);
				}
				pView.eventTransactionAsyncOperationComplete(pTransactionGUID, tmpRefreshEvent);
				return;
			}
			let tmpRecordPayload;
			if (pInput.PictForm.Multiple)
			{
				tmpRecordPayload = this._alignRecordsToRequestedValues(pRecords || [], pIDValue, tmpRecordIdentityField, pEntity);
			}
			else
			{
				if (pRecords.length > 1)
				{
					this.pict.log.warn(`Multiple ${pEntity} records found with ${tmpRecordIdentityField} = ${pIDValue}. Using the first record.`);
				}
				const tmpHeldRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpRecordAddress);
				tmpRecordPayload = pRecords[0] ? this._reconcileRefreshedRecord(pView, pInput, tmpHeldRecord, pRecords[0], tmpRecordIdentityField, pIDValue, pRowIndex) : (tmpHeldRecord || { });
			}
			this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpRecordAddress, tmpRecordPayload);
			this._onRecordRefresh(pView, pInput, tmpRecordPayload, tmpRecordAddress, pTransactionGUID, pRowIndex);
			if (typeof fCallback === 'function')
			{
				fCallback(tmpRecordPayload);
			}
			pView.eventTransactionAsyncOperationComplete(pTransactionGUID, tmpRefreshEvent);
		};
		if (libSharedEntityRead.isEmptyIdentityValue(tmpRecordIdentityField, pIDValue))
		{
			fOnRecords(null, [ ]);
			return;
		}
		libSharedEntityRead.readJSON(this.pict, `${pEntity}s/FilteredTo/FBL~${tmpRecordIdentityField}~INN~${encodeURIComponent(pIDValue)}`, { ReuseResult: false }, fOnRecords);
	}

	/**
	 * Choose what a single-record refresh writes: the server row, or the record the form already holds.
	 *
	 * A re-fetch of the record already selected is server state, not an edit, so the baseline moves with
	 * the fields the server changed (`EntityDataBaseline.classifyRecordRefresh`). When the held record
	 * carries a change the server does not have yet, the row is not written; the record is flagged out of
	 * date instead, and the manual refresh stays the user's choice. A changed selection always takes the row.
	 * `MaterialsCascadePicker._reconcileRefreshedRecord` is the Picker family's copy; the two must agree.
	 *
	 * @param {Object} pView @param {Object} pInput
	 * @param {Record<string, any>|null} pHeldRecord - The record at the address when the row arrived.
	 * @param {Record<string, any>} pFetchedRecord - The server row.
	 * @param {string} pRecordIdentityField @param {any} pIDValue @param {number|string|null} [pRowIndex]
	 * @return {Record<string, any>} - The record to write.
	 */
	_reconcileRefreshedRecord(pView, pInput, pHeldRecord, pFetchedRecord, pRecordIdentityField, pIDValue, pRowIndex)
	{
		if (!this._isScopedEntityDataEnabled() || !pHeldRecord || typeof pHeldRecord !== 'object' || Array.isArray(pHeldRecord) || pHeldRecord[pRecordIdentityField] != pIDValue)
		{
			return pFetchedRecord;
		}
		const tmpEntity = this._getEntity(pInput);
		const tmpGUID = pFetchedRecord[`GUID${tmpEntity}`] || pHeldRecord[`GUID${tmpEntity}`];
		const tmpContext = this._getComprehensionContext(pInput);
		const tmpDecision = EntityDataBaseline.classifyRecordRefresh(this.pict, tmpContext, tmpEntity, tmpGUID, pHeldRecord, pFetchedRecord);
		if (tmpDecision.Action === 'keep')
		{
			this.pict.log.info(`MaterialsEntitySelector: kept the held ${tmpEntity} ${tmpGUID} over the server row: ${tmpDecision.Reason}.`);
			const tmpKey = this._desyncKey(pInput, pRowIndex);
			this._desyncState()[tmpKey] = 'stale';
			this._recordDesyncFetched[tmpKey] = pFetchedRecord;
			this._renderDesyncAffordance(pView, pInput, /** @type {number} */ (pRowIndex));
			return pHeldRecord;
		}
		if (tmpDecision.Action === 'adopt')
		{
			EntityDataBaseline.adoptRefreshedFields(this.pict, tmpContext, tmpEntity, tmpGUID, pFetchedRecord, tmpDecision.Fields);
		}
		return pFetchedRecord;
	}

	/**
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {Object} pRecord - The record that was refreshed.
	 * @param {string} pRecordAddress - The address where the record was stored.
	 * @param {string} pTransactionGUID - A transaction GUID to associate with this operation.
	 * @param {number} [pRowIndex] - The row index. If present, this is a tabular input.
	 */
	_onRecordRefresh(pView, pInput, pRecord, pRecordAddress, pTransactionGUID, pRowIndex)
	{
		const tmpEvent = `${this._getEntity(pInput)}Changed:${this._getTrackingGUID(pInput, pRowIndex)}:${this._getRecordAddress(pView, pInput, pRowIndex)}`;
		this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(tmpEvent, pTransactionGUID);
		if (pInput.PictForm.FireTriggerGroupOnRecordRefresh)
		{
			const tmpEmitterHash = pInput.Hash || pInput.DataAddress;
			// Hydration guard: this record-refresh fan-out is a SYNCHRONOUS cascade (the trigger group plus every
			// sibling autofill it emits). A load is authoritative — persisted values must display, not be re-vetted
			// against the current filter scope — so mark the fan-out; the autofill-scope vet skips while it is set
			// (see onAfterEventCompletion / onAfterEventTabularCompletion). Counter handles re-entrant refreshes;
			// finally guarantees reset even if a handler throws.
			this.pict._MaterialsEntityHydrationDepth = (this.pict._MaterialsEntityHydrationDepth || 0) + 1;
			try
			{
				if (pRowIndex != null)
				{
					// Tabular anchor: include the emitting row index so a dependent picker
					// clears ONLY its own row, not every row of the table (see
					// _matchedDynamicFilterForEventTabular). The global broadcast still fans
					// this out to every row's onAfterEventTabularCompletion; the row index in
					// slot [4] is what lets each row decide whether the change was its own.
					this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(`TriggerGroup:${pInput.PictForm.FireTriggerGroupOnRecordRefresh}:RecordRefresh:${tmpEmitterHash}:${pRowIndex}:${this.pict.getUUID()}`);
				}
				else
				{
					this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(`TriggerGroup:${pInput.PictForm.FireTriggerGroupOnRecordRefresh}:RecordRefresh:${tmpEmitterHash}:${this.pict.getUUID()}`);
				}
			}
			finally
			{
				this.pict._MaterialsEntityHydrationDepth--;
			}
		}
	}

	/**
	 * Fire the save-time EntityData projection when this provider has at least one creation-enabled
	 * input (latched in _isEntityCreationEnabled). Mirrors the trigger the Test selector owns, so a
	 * creating base selector persists its records on a form with no Test selector present. Providers'
	 * onBeforeSaveDataAsync run once each, sequentially (Anticipate waterfall ordered by
	 * AutoSaveDataOrdinal); when both this and the Test selector are present each fires once and every
	 * projector re-collects from its current __Record, so the redundant pass is idempotent. No-op (and
	 * no empty _EntityData reset) for a provider with no creation-enabled input — default behavior.
	 * @param {(pError?: Error) => void} fCallback
	 */
	onBeforeSaveDataAsync(fCallback)
	{
		if (!this._entityCreationEnabled)
		{
			return super.onBeforeSaveDataAsync(fCallback);
		}
		if (!this._isScopedEntityDataEnabled())
		{
			this.pict.manifest.setValueAtAddress(this.pict, 'AppData.DocumentData.ReportData.FormData._EntityData', { });
		}
		const tmpEvent = 'ProjectEntityDataRecords:AppData.DocumentData.ReportData.FormData._EntityData';
		const tmpTransactionGUID = this.pict.getUUID();
		/** @type {any} */ (this.pict).TransactionTracking.registerTransaction(tmpTransactionGUID);
		this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(tmpEvent, tmpTransactionGUID);
		this.pict.views.PictFormMetacontroller.registerOnTransactionCompleteCallback(tmpTransactionGUID, () =>
		{
			this._sanitizeProjectedComprehensions();
			super.onBeforeSaveDataAsync(fCallback);
		});
		this.pict.views.PictFormMetacontroller.finalizeTransaction(tmpTransactionGUID);
	}

	/**
	 * The object this input projects its created records into on the save sweep, keyed
	 * `<Entity>.<GUID>`. THE ONE PLACE the scoped/legacy decision is made on the emission side: the
	 * projectors below take the object and are identical on both paths, because a context root and
	 * the legacy ledger have the same shape.
	 *
	 * Scoped roots are remembered for the post-sweep sanitize pass.
	 *
	 * @param {Object} pInput - The form input descriptor.
	 * @param {string} pEvent - The sweep event.
	 * @return {Record<string, any>|null} - The projection target, or null when this is not a sweep
	 *   event or no target resolves (never fabricates one).
	 */
	_getProjectionTarget(pInput, pEvent)
	{
		if (typeof pEvent !== 'string')
		{
			return null;
		}
		const [ tmpEventType, tmpTargetAddress ] = pEvent.split(':');
		if (tmpEventType !== 'ProjectEntityDataRecords')
		{
			return null;
		}
		if (this._isScopedEntityDataEnabled())
		{
			const tmpContext = this._getComprehensionContext(pInput);
			const tmpContextRoot = ScopedEntityData.resolveContextRoot(this.pict, tmpContext);
			// A baseline sweep collects into a detached scratch tree: nothing was emitted, so neither the
			// FormEntityCreator cross-check nor the post-sweep delta pass may be armed by it.
			if (tmpContextRoot && !ScopedEntityData.getBaselineSweepTarget(this.pict))
			{
				ScopedEntityData.markActive(this.pict);
				if (!this._scopedProjectionRoots)
				{
					this._scopedProjectionRoots = new Map();
				}
				this._scopedProjectionRoots.set(tmpContext, tmpContextRoot);
			}
			return tmpContextRoot;
		}
		if (!tmpTargetAddress)
		{
			return null;
		}
		const tmpTarget = this.pict.manifest.getValueAtAddress(this.pict, tmpTargetAddress);
		if (!tmpTarget || typeof tmpTarget !== 'object' || Array.isArray(tmpTarget))
		{
			return null;
		}
		return tmpTarget;
	}

	/**
	 * Narrow every scoped root this provider projected into during the sweep: strip transient `__` UI
	 * state, then reduce each record to what changed since the baseline. Runs once, after the sweep,
	 * rather than at each write — the projectors key the form's LIVE record objects in, so acting
	 * earlier would either mutate the record the form renders from or force every projector to clone.
	 */
	_sanitizeProjectedComprehensions()
	{
		// Also the roots the picker family noted (this family never notes any): on a form that runs the
		// pickers, this provider's sweep re-projects their records and would otherwise send them whole.
		const tmpRoots = new Map(this._scopedProjectionRoots || []);
		for (const [ tmpContext, tmpContextRoot ] of ScopedEntityData.takeNotedProjectionRoots(this.pict))
		{
			tmpRoots.set(tmpContext, tmpContextRoot);
		}
		if (tmpRoots.size < 1)
		{
			return;
		}
		// Handed to the document save, which folds every provider's emission ONCE just before the PUT.
		// Folding here instead would advance the baseline mid-save, and the next provider's prune over
		// this same root would then suppress every record and send an empty comprehension.
		const tmpEmitted = new Map();
		for (const [ tmpContext, tmpContextRoot ] of tmpRoots)
		{
			EntityDataBaseline.pruneContextRootToDelta(this.pict, tmpContext, tmpContextRoot);
			tmpEmitted.set(tmpContext, tmpContextRoot);
		}
		if (this._scopedProjectionRoots)
		{
			this._scopedProjectionRoots.clear();
		}
		tmpEmitted.forEach((pContextRoot, pContext) => EntityDataBaseline.recordPendingEmission(this.pict, pContext, pContextRoot));
	}

	/**
	 * Ask for the load-settled baseline capture on this render transaction. Idempotent across every
	 * input on the form and across both input families — the first to ask schedules it.
	 * @param {Object} pView @param {string} pTransactionGUID
	 */
	_scheduleBaselineCapture(pView, pTransactionGUID)
	{
		if (!this._isScopedEntityDataEnabled())
		{
			return;
		}
		EntityDataBaseline.scheduleLoadBaselineCapture(this.pict, pView, pTransactionGUID);
	}


	/**
	 * Generic save-time projection handler. On the ProjectEntityDataRecords sweep, write this input's
	 * created __Record into the target _getProjectionTarget resolved (keyed by GUID<Entity> inside
	 * _syncRecordToEntityData), plus any sibling DocumentPolyJoin. Opt-in via AllowEntityCreation;
	 * opt-out per input via SkipEntityDataProjection. No-op for non-creating selectors, so default
	 * behavior is unchanged. _syncRecordToEntityData itself no-ops unless the record carries a
	 * GUID<Entity>, a second safety gate. Subclasses that already project via their own _handleEvent
	 * (e.g. Sample) re-run this idempotently through super — same target object, same GUID key.
	 * @param {Object} pView @param {Object} pInput @param {number|string|null} pRowIndex
	 * @param {string} pEvent @param {string} pTransactionGUID
	 */
	_handleBaseSaveProjectionEvent(pView, pInput, pRowIndex, pEvent, pTransactionGUID)
	{
		if (typeof pEvent !== 'string' || !this._isEntityCreationEnabled(pInput) || pInput?.PictForm?.SkipEntityDataProjection)
		{
			return;
		}
		const tmpTarget = this._getProjectionTarget(pInput, pEvent);
		if (!tmpTarget)
		{
			return;
		}
		this._syncRecordToEntityData(pView, pInput, pRowIndex, pTransactionGUID, tmpTarget);
		const tmpRecordAddress = this._getRecordAddress(pView, pInput, pRowIndex);
		const tmpDocumentPolyJoinAddress = pInput.PictForm?.DocumentPolyJoinAddress || `${tmpRecordAddress}_DocumentPolyJoin`;
		const tmpDocumentPolyJoin = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpDocumentPolyJoinAddress);
		if (tmpDocumentPolyJoin && typeof tmpDocumentPolyJoin === 'object' && !Array.isArray(tmpDocumentPolyJoin) && tmpDocumentPolyJoin.GUIDDocumentPolyJoin)
		{
			if (!tmpTarget.DocumentPolyJoin || typeof tmpTarget.DocumentPolyJoin !== 'object' || Array.isArray(tmpTarget.DocumentPolyJoin))
			{
				tmpTarget.DocumentPolyJoin = { };
			}
			tmpTarget.DocumentPolyJoin[tmpDocumentPolyJoin.GUIDDocumentPolyJoin] = tmpDocumentPolyJoin;
		}
	}

	/**
	 * Whether this provider emits scoped `Document.EntityData` comprehensions instead of the legacy
	 * `FormData._EntityData` ledger. The same flag stands `FormEntityCreator` down, so the two
	 * paths never run together.
	 * @return {boolean}
	 */
	_isScopedEntityDataEnabled()
	{
		return ScopedEntityData.isEnabled(this.pict);
	}

	/**
	 * The trigger context this input's records are applied under. Scheduling is manifest data:
	 * `PictForm.ComprehensionContext` selects it, the provider default (`OnSave`) applies otherwise.
	 * @param {Object} pInput
	 * @return {string}
	 */
	_getComprehensionContext(pInput)
	{
		return ScopedEntityData.resolveContext(pInput, this.options?.ComprehensionContext || ScopedEntityData.DEFAULT_CONTEXT);
	}


	/**
	 * Whether a sample-join refresh may rewrite this input's joins. A frozen document keeps the joins it
	 * saved; an input that must follow the database even then sets `PictForm.RefreshJoinsWhenLocked: true`.
	 * @param {Object} pInput
	 * @return {boolean}
	 */
	_shouldRefreshSampleJoins(pInput)
	{
		return !this._isRecordFrozen() || pInput?.PictForm?.RefreshJoinsWhenLocked === true;
	}

	/**
	 * The empty-result branch of a sample-join refresh: drop the held joins whose rows were soft-deleted
	 * since the document was saved, and their values from the selection. Only a row read back as deleted
	 * is dropped (`ScopedEntityData.resolveSoftDeletedJoins`); anything else keeps the persisted value. The
	 * drop is a load, so no deletion is staged, and it re-salts the input so a re-add mints a GUID the
	 * deleted row does not own. An input with `RefreshOnOpen: false` keeps its saved snapshot.
	 *
	 * The drop is applied to the joins and value the input holds when the reads settle
	 * (`ScopedEntityData.applySoftDeletedDrop`), not to the ones it started from, so a refresh that landed
	 * meanwhile is not undone and an overlapping drop that already removed the links writes nothing.
	 * @param {Object} pView @param {Object} pInput @param {number|string|null} pRowIndex
	 * @param {{ Entity: string, ValueField: string, JoinAddress: string, Value: any, Label: string }} pJoinScope
	 * @param {(pDropped: number) => void} fDone - Called once, after any write, with the number of held links
	 *   found deleted.
	 */
	_dropSoftDeletedHeldJoins(pView, pInput, pRowIndex, pJoinScope, fDone)
	{
		if (pInput.PictForm?.RefreshOnOpen === false)
		{
			return fDone(0);
		}
		const tmpHeldJoins = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), pJoinScope.JoinAddress);
		ScopedEntityData.resolveSoftDeletedJoins(this.pict, pJoinScope.Entity, pJoinScope.ValueField, tmpHeldJoins, pJoinScope.Value, (pResult) =>
		{
			if (pResult.Dropped.length < 1)
			{
				return fDone(0);
			}
			const tmpDestination = pView.getMarshalDestinationObject();
			const tmpDrop = ScopedEntityData.applySoftDeletedDrop(pJoinScope.Entity, pJoinScope.ValueField,
				this.pict.manifest.getValueAtAddress(tmpDestination, pJoinScope.JoinAddress),
				this.pict.manifest.getValueAtAddress(tmpDestination, this._getValueAddress(pView, pInput, pRowIndex)),
				pResult);
			if (!tmpDrop.Changed)
			{
				return fDone(pResult.Dropped.length);
			}
			ScopedEntityData.beginProgrammaticLoad(this.pict);
			try
			{
				if (tmpDrop.Removed.length > 0)
				{
					this.pict.manifest.setValueAtAddress(tmpDestination, pJoinScope.JoinAddress, tmpDrop.Joins);
				}
				const tmpRecordAddress = this._getRecordAddress(pView, pInput, pRowIndex);
				const tmpHeldRecords = this.pict.manifest.getValueAtAddress(tmpDestination, tmpRecordAddress);
				const tmpKeptRecords = ScopedEntityData.pruneRecordsToValue(tmpHeldRecords, pJoinScope.ValueField, tmpDrop.Value);
				if (tmpKeptRecords !== tmpHeldRecords)
				{
					this.pict.manifest.setValueAtAddress(tmpDestination, tmpRecordAddress, tmpKeptRecords);
				}
				if (tmpDrop.ValueChanged)
				{
					if (pRowIndex != null)
					{
						pView.setDataTabularByHash(pInput.PictForm.GroupIndex, pInput.Hash, pRowIndex, tmpDrop.Value);
					}
					else
					{
						pView.setDataByInput(pInput, tmpDrop.Value);
					}
				}
			}
			finally
			{
				ScopedEntityData.endProgrammaticLoad(this.pict);
			}
			this._generateSaltIfNeeded(pView, pInput, pRowIndex, true);
			this.log.info(`${pJoinScope.Label}: dropped ${tmpDrop.Removed.length} link(s) whose record was deleted since the document was saved: ${pResult.Dropped.join(', ')}; value now [${tmpDrop.Value}].`);
			return fDone(pResult.Dropped.length);
		});
	}

	/**
	 * A creation-enabled selection was cleared. Two things follow, both only under the scoped path:
	 * tombstones for the rows this selector owns (`_collectRemovedDependentRecords`; never the selected
	 * record itself), and a FRESH `__Salt` so a re-add mints a NEW GUID — an upsert cannot un-delete a
	 * soft-deleted row, so re-using the GUID would upsert into the tombstoned row. No-op unless the
	 * cleared record actually carried its client GUID.
	 * @param {Object} pView @param {Object} pInput @param {number|string|null} [pRowIndex]
	 */
	_handleEntitySelectionCleared(pView, pInput, pRowIndex)
	{
		if (!this._isScopedEntityDataEnabled() || !this._isEntityCreationEnabled(pInput))
		{
			return;
		}
		const tmpEntity = this._getEntity(pInput);
		const tmpRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), this._getRecordAddress(pView, pInput, pRowIndex));
		const tmpGUID = (tmpRecord && typeof tmpRecord === 'object' && !Array.isArray(tmpRecord)) ? tmpRecord[`GUID${tmpEntity}`] : null;
		if (!tmpGUID)
		{
			return;
		}
		this._stageRemovalTombstones(pInput, this._collectRemovedDependentRecords(pView, pInput, pRowIndex, new Set()), 'clear');
		this._generateSaltIfNeeded(pView, pInput, pRowIndex, true);
	}

	/**
	 * Stage tombstones for a set of removed records and report what happened. Every removal route in
	 * this family funnels through here, so the gates (persisted, document-owned) and the logging are
	 * applied once. A record the guard would refuse is logged at debug and NOT staged — see
	 * `docs/plans/entitydata-round2/05-client-delete-completeness.md` §4.
	 * @param {Object} pInput @param {Array<{ Entity: string, Record: Record<string, any> }>} pRemovals
	 * @param {string} pReason - What removed them, for the log line.
	 * @return {number} - How many tombstones were staged.
	 */
	_stageRemovalTombstones(pInput, pRemovals, pReason)
	{
		if (!this._isScopedEntityDataEnabled() || !Array.isArray(pRemovals) || pRemovals.length < 1)
		{
			return 0;
		}
		const tmpContext = this._getComprehensionContext(pInput);
		const tmpResult = ScopedEntityData.tombstoneRemovedRecords(this.pict, tmpContext, pRemovals);
		if (tmpResult.Staged.length > 0)
		{
			this.pict.log.info(`MaterialsEntitySelector[${pInput.Hash || pInput.DataAddress}]: staged deletion of ${tmpResult.Staged.length} record(s) on ${pReason}.`, { Records: tmpResult.Staged });
		}
		return tmpResult.Staged.length;
	}

	/**
	 * The identity values a committed selection retains, as a Set of strings. The value list IS the
	 * identity list for a Multiple selector (`_alignRecordsToRequestedValues` depends on the same
	 * property).
	 * @param {any} pValue @return {Set<string>}
	 */
	_getRetainedIdentitySet(pValue)
	{
		const tmpRetained = new Set();
		if (pValue == null || pValue === '')
		{
			return tmpRetained;
		}
		for (const tmpToken of String(pValue).split(','))
		{
			const tmpTrimmed = tmpToken.trim();
			if (tmpTrimmed.length > 0)
			{
				tmpRetained.add(tmpTrimmed);
			}
		}
		return tmpRetained;
	}

	/**
	 * The rows this selector owns for a de-selected value — the rows correlated to its selection, never the
	 * selected record. Subclasses declare their own (TestInstance units, `SampleLabJoin`s, `SampleLineItemJoin`s);
	 * the base owns the document's `DocumentPolyJoin` to the selection, which goes when nothing stays selected.
	 * @param {Object} pView @param {Object} pInput @param {number|string|null} pRowIndex
	 * @param {Set<string>} pRetainedIdentities - The identities still selected.
	 * @return {Array<{ Entity: string, Record: Record<string, any> }>}
	 */
	_collectRemovedDependentRecords(pView, pInput, pRowIndex, pRetainedIdentities)
	{
		if (pRetainedIdentities.size > 0)
		{
			return [ ];
		}
		const tmpAddress = pInput.PictForm?.DocumentPolyJoinAddress || `${this._getRecordAddress(pView, pInput, pRowIndex)}_DocumentPolyJoin`;
		return ScopedEntityData.collectTestInstanceUnit({ DocumentPolyJoin: this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpAddress) });
	}

	/**
	 * The de-selection seam. A Multiple selector expresses removal by committing a SHORTER value
	 * list, which `_pruneUnselectedOptions` then reflects in the DOM and `_refreshRecord` replaces
	 * `__Record` from — so the only moment the dropped records still exist is before that runs.
	 *
	 * Suppressed during a programmatic load: `setDataByInput` routes into `onDataChange`, so an
	 * adoption (`_loadSampleTests`) or a `SampleChanged` re-bind arrives here looking exactly like a
	 * user de-selection. Those detach records from the form without removing them from the sample,
	 * which is why they must stage nothing.
	 *
	 * Anything removed also rotates `__Salt`, so a later re-add of the same value mints a new GUID
	 * rather than the removed row's. Kept records carry their stored GUIDs and are never re-derived
	 * from the salt, so the rotation only reaches records minted after it.
	 * @param {Object} pView @param {Object} pInput @param {any} pValue @param {number|string|null} [pRowIndex]
	 */
	_handleSelectionRemovals(pView, pInput, pValue, pRowIndex)
	{
		if (!this._isScopedEntityDataEnabled() || ScopedEntityData.isProgrammaticLoad(this.pict))
		{
			return;
		}
		const tmpRetained = this._getRetainedIdentitySet(pValue);
		const tmpRemovals = this._collectRemovedDependentRecords(pView, pInput, pRowIndex, tmpRetained);
		this._stageRemovalTombstones(pInput, tmpRemovals, 'de-selection');
		if (tmpRemovals.length > 0)
		{
			this._generateSaltIfNeeded(pView, pInput, pRowIndex, true);
		}
	}

	/**
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {number|string|null} pRowIndex - The row index of the tabular data.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @param {Record<string, Record<string, any>>} pTargetObject - The `<Entity>.<GUID>` object to project into.
	 */
	_syncRecordToEntityData(pView, pInput, pRowIndex, pTransactionGUID, pTargetObject)
	{
		const tmpEntity = this._getEntity(pInput);
		const tmpRecordAddress = this._getRecordAddress(pView, pInput, pRowIndex);
		const tmpRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpRecordAddress);
		if (!tmpRecord || typeof tmpRecord !== 'object' || Array.isArray(tmpRecord) || !tmpRecord[`GUID${tmpEntity}`])
		{
			return;
		}
		const tmpEntityData = pTargetObject;
		if (!tmpEntityData || typeof tmpEntityData !== 'object' || Array.isArray(tmpEntityData))
		{
			return;
		}

		if (!tmpEntityData[tmpEntity] || typeof tmpEntityData[tmpEntity] !== 'object' || Array.isArray(tmpEntityData[tmpEntity]))
		{
			tmpEntityData[tmpEntity] = { };
		}
		tmpEntityData[tmpEntity][tmpRecord[`GUID${tmpEntity}`]] = tmpRecord;
	}

	/**
	 * Remove only the <option>s that are no longer selected, leaving the rest untouched.
	 *
	 * The multi-select marshal used to `.empty()` the whole element and let
	 * _renderEntitiesToPicker rebuild it. Functionally fine, visually not: select2 repaints
	 * from the element, so every tag vanishes and comes back -- and the marshal fan-out runs
	 * it several times per change.
	 *
	 * Pruning is the half `.empty()` was actually needed for (dropping de-selected values);
	 * adding back is already _renderEntitiesToPicker's job and it appends only what is
	 * missing. Doing both incrementally means the picker is never empty.
	 *
	 * @param {string} pSelector - The select element's selector.
	 * @param {Array<string>} pValues - The values that should remain.
	 * @return {number} - How many options were removed.
	 */
	_pruneUnselectedOptions(pSelector, pValues)
	{
		const tmpElement = window['jQuery'](pSelector)[0];
		if (!tmpElement || !tmpElement.options)
		{
			return 0;
		}
		const tmpKeep = {};
		for (let i = 0; i < (pValues || []).length; i++)
		{
			tmpKeep[String(pValues[i])] = true;
		}
		let tmpRemoved = 0;
		// Backwards: removing shifts the live options collection.
		for (let i = tmpElement.options.length - 1; i >= 0; i--)
		{
			if (!tmpKeep[String(tmpElement.options[i].value)])
			{
				tmpElement.remove(i);
				tmpRemoved++;
			}
		}
		return tmpRemoved;
	}

	/**
	 * Reduce a Multiple selector's hydrated records to exactly ONE per selected value.
	 *
	 * The hydration query (`<Entity>s/FilteredTo/FBL~<identity>~INN~<values>`) is not scoped
	 * to a customer, so an identity field that is not globally unique returns a row per
	 * MATCH rather than per SELECTED VALUE. Seen on LADOTD Sample Creation: `SampleTestIDs`
	 * holds Test HASHES, and `FLDGLB01v1`/`LABGLB01v1` each exist for IDCustomer 10 AND 114,
	 * so two selected tests hydrated four records -- which the test-instance builder then
	 * turned into four table rows ("sometimes there are two tests instead of one").
	 *
	 * The single-value branch below has always collapsed this ("Using the first record"); the
	 * Multiple branch kept every row. A Multiple selector's records must be 1:1 with its
	 * values -- the value list IS the identity list.
	 *
	 * ⚠️ Ambiguity is resolved by taking the FIRST match, matching the single-value branch,
	 * and warns when it has to choose. It cannot prefer the "right" customer: there is no
	 * reliable client-side customer here (`CurrentUser.CustomerID` is the logged-in user's,
	 * which is 1 for internal staff, not the document's). Scoping the query -- or the
	 * endpoint -- is the real fix; this keeps the form self-consistent meanwhile.
	 *
	 * Fails OPEN: if nothing lines up (an identity field that is not what the value list
	 * holds) the original records are returned untouched rather than blanking the field.
	 *
	 * @param {Array<any>} pRecords - The hydrated records.
	 * @param {any} pIDValue - The selected value(s), comma-delimited.
	 * @param {string} pRecordIdentityField - The field the values are matched against.
	 * @param {string} pEntity - Entity name, for logging.
	 * @return {Array<any>} - One record per selected value, in the selected order.
	 */
	_alignRecordsToRequestedValues(pRecords, pIDValue, pRecordIdentityField, pEntity)
	{
		const tmpRequested = String((pIDValue === null || pIDValue === undefined) ? '' : pIDValue)
			.split(',').map((pValue) => pValue.trim()).filter((pValue) => pValue !== '');
		if (!tmpRequested.length || !Array.isArray(pRecords) || !pRecords.length)
		{
			return pRecords;
		}
		const tmpAligned = [];
		for (let i = 0; i < tmpRequested.length; i++)
		{
			const tmpMatches = pRecords.filter((pRecord) => pRecord && (String(pRecord[pRecordIdentityField]) === tmpRequested[i]));
			if (!tmpMatches.length)
			{
				continue;
			}
			if (tmpMatches.length > 1)
			{
				this.pict.log.warn(`Multiple ${pEntity} records share ${pRecordIdentityField} = ${tmpRequested[i]} (${tmpMatches.length} found); using the first. The hydration query is not customer-scoped.`);
			}
			tmpAligned.push(tmpMatches[0]);
		}
		// Nothing matched -- the identity field is not what the value list holds. Leave it alone.
		return tmpAligned.length ? tmpAligned : pRecords;
	}

	/**
	 * Safely read a select2 element's current selection, returning [] when the element isn't
	 * select2-initialized yet. The form can marshal a value before the picker's select2 finishes
	 * initializing; calling `.select2('data')` then logs "called on an element not using Select2"
	 * and throws "Cannot read properties of undefined (reading 'data')". Gate on the select2 instance.
	 *
	 * @param {string} pSelector
	 * @return {Array<any>}
	 */
	_safeSelect2Data(pSelector)
	{
		const tmp$ = window['jQuery'](pSelector);
		return (tmp$.data('select2') ? tmp$.select2('data') : null) || [];
	}
}

module.exports = MaterialsEntityInputType;
